import React, { useState, useEffect, useRef, useReducer, useCallback, useMemo } from 'react'
import { usePageContext } from 'vike-react/usePageContext'
import { langCodeToName } from '../../utils/appTheme.js'
import { recordAttempt, recordVocabularyAttempt } from '../../utils/progress.js'
import {
  loadVerbData, generateDrillCards, getVerbType, deriveConjugation,
  TENSE_TITLE_PT, TENSE_TITLE_FI,
  type DrillCardData, type ConjTense,
} from '../../utils/conjugation.js'
import { MotifBand, MotifWatermark, motifLabel } from '../../components/Motifs.js'
import { Plant } from '../../components/Flora.js'
import type { Language } from '../../components/Scenes.js'
import {
  BIOMES, TRAITS, biomeById, traitById,
  readGarden, writeGarden, resetGarden, masteredSpecimens, availableBiomes,
  isBiomeUnlocked, comboMultiplier, gardenLevel, hashString,
  rarityOf, RARITY_META, TRAIT_RARITY_META,
  MASTERY_THRESHOLD, VITALITY_TIERS, VITALITY_COSTS, COMPOST_TIERS, COMPOST_COSTS,
  NORMAL_GROVES, GROVE_COUNT, BOSS_BASE_CARDS, BOSS_POLLEN, HEARTWOOD_PER_BOSS,
  depthConfig, MAX_DEPTH, OLD_GROWTH, oldGrowthCost,
  type GardenState, type Biome, type Trait, type BiomeId, type TraitId, type Specimen,
  type Rarity, type DiscoveredSpecies, type TraitRarity,
} from '../../utils/garden.js'
import { computeVerbLeaderboard, getWordsNeedingReview, computeCEFR } from '../../utils/progress.js'
import { sound } from '../../utils/garden-sound.js'
import {
  ACTS, COMPANION, currentAct, nextAct, actObjective, restorationPct, hushPct, storyPoints,
  pendingBeats, type Act, type Beat,
} from '../../utils/garden-story.js'
import {
  ROSTER, AVATARS, ACHIEVEMENTS, leaderboard, youAsPlayer, wardenTitle, wardenScore,
  evaluateAchievements, unlockedAchievements, newlyUnlocked, readProfile, writeProfile,
  type Player, type Profile, type Achievement,
} from '../../utils/garden-social.js'
import {
  ConjugardenProvider, useMe, useLeaderboard, useFriends, useSearchWardens, useWardenProfile,
  useUpdateProfile, useToggleFriend, useSyncGarden, authUrls, type LeaderboardEntry,
} from '../../utils/conjugardenApi.js'
import type { GardenSummary } from '../../shared/conjugarden-api.js'

// Build the compact per-language snapshot the server ranks Wardens on.
function summaryFromGarden(state: GardenState, lang: string): GardenSummary {
  const biomesCleared = Object.values(state.clears).filter(c => (c ?? 0) > 0).length
  return {
    lang,
    restoration: restorationPct(state),
    specimens: masteredSpecimens(state).length,
    biomesCleared,
    bestStreak: state.stats.bestStreak,
    pollen: state.stats.totalPollen,
    wins: state.stats.wins,
    totalCorrect: state.stats.totalCorrect,
    updatedAt: Date.now(),
  }
}

// ── Design tokens (shared with the rest of the app) ──────────────────────────
const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)', surf3: 'var(--surf3)',
  border: 'var(--border)', border2: 'var(--border2)',
  text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accent: 'var(--accent)', accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
  green: 'var(--success)', greenBg: 'var(--success-bg)', greenBorder: 'var(--success-border)',
  red: 'var(--error)', redBg: 'var(--error-bg)', redBorder: 'var(--error-border)',
  amber: 'var(--warn)', amberBg: 'var(--warn-bg)', amberBorder: 'var(--warn-border)',
  blue: 'var(--blue)', blueBg: 'var(--blue-bg)',
}
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
}

const PRONOUNS_ES = ['yo', 'tú', 'él / ella', 'nosotros', 'vosotros', 'ellos']
const PRONOUNS_PT = ['eu', 'você', 'ele/ela', 'nós', 'vocês', 'eles/elas']
const PRONOUNS_FI = ['minä', 'sinä', 'hän', 'me', 'te', 'he']
const pronounsFor = (lang: string) => lang === 'pt' ? PRONOUNS_PT : lang === 'fi' ? PRONOUNS_FI : PRONOUNS_ES

const FULL_TENSES: ConjTense[] = ['present', 'preterite', 'imperfect', 'future', 'conditional', 'present_subjunctive', 'imperfect_subjunctive', 'present_perfect', 'pluperfect']
const SUPPORTED_TENSES: Record<string, ConjTense[]> = { fi: ['present', 'conditional', 'present_perfect'] }
const supportedFor = (lang: string) => new Set(SUPPORTED_TENSES[lang] ?? FULL_TENSES)

const CARDS_PER_GROVE = 5
const BASE_SPORES = 10
const MASTERY_BONUS = 45

// ── SSR-safe mobile + mount hooks ────────────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(false)
  useEffect(() => {
    const c = () => setM(window.innerWidth < 768)
    c(); window.addEventListener('resize', c)
    return () => window.removeEventListener('resize', c)
  }, [])
  return m
}
function useMounted() {
  const [m, setM] = useState(false)
  useEffect(() => setM(true), [])
  return m
}

// ── Tiny shared bits ─────────────────────────────────────────────────────────
function Kbd({ children }: { children: string }) {
  return <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: AF.m, fontSize: 10, color: C.muted, background: C.surf2,
    border: `1px solid ${C.border}`, borderBottomWidth: 2, padding: '1px 5px',
    borderRadius: 4, lineHeight: 1.5, userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0,
  }}>{children}</span>
}

function PollenPill({ amount }: { amount: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, background: C.accentBg, border: `1px solid ${C.accentBorder}` }}>
      <span style={{ fontSize: 13 }}>🌸</span>
      <span style={{ fontFamily: AF.m, fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{amount.toLocaleString()}</span>
      <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, letterSpacing: '0.08em' }}>POLLEN</span>
    </div>
  )
}

// Plant glyphs now live in components/Flora.tsx — styled to the language you're
// learning (Andalusian / Brazilian / Nordic species). Imported as `Plant`.

function GardenPlot({ specimens, clears, height, language }: { specimens: Specimen[]; clears: number; height: number; language: Language }) {
  const W = 720
  const groundY = height - 30
  const count = specimens.length
  // Lay plants across up to 3 depth rows; back rows sit higher, smaller, fainter.
  const rows = count <= 7 ? 1 : count <= 18 ? 2 : 3
  const placed = useMemo(() => {
    const items: { x: number; y: number; scale: number; opacity: number; seed: number }[] = []
    const perRow = Math.ceil(count / rows) || 1
    specimens.forEach((s, i) => {
      const row = Math.floor(i / perRow)
      const inRow = i % perRow
      const rowCount = Math.min(perRow, count - row * perRow)
      const seed = hashString(s.verb)
      const pad = 60
      const span = W - pad * 2
      const x = rowCount === 1 ? W / 2 : pad + (span * inRow) / (rowCount - 1)
      const depth = rows > 1 ? row / (rows - 1) : 0           // 0 front … 1 back
      const y = groundY - depth * (height * 0.42)
      const scale = 1.5 - depth * 0.55 + ((seed >> 5) % 5) * 0.04
      const opacity = 1 - depth * 0.42
      items.push({ x, y, scale, opacity, seed })
    })
    // Render back-to-front.
    return items.sort((p, q) => p.y - q.y || q.scale - p.scale)
  }, [specimens, count, rows, groundY, height])

  const emptySlots = Math.max(0, Math.min(7, 7 - Math.min(count, 7)))

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} style={{ display: 'block' }} aria-label="Your living garden">
      <defs>
        <linearGradient id="cg-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--accent)" stopOpacity="0.05" />
          <stop offset="1" stopColor="var(--success)" stopOpacity="0.02" />
        </linearGradient>
        <radialGradient id="cg-sun" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="var(--accent)" stopOpacity="0.5" />
          <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width={W} height={height} fill="url(#cg-sky)" />
      <circle cx={W - 80} cy={46} r="46" fill="url(#cg-sun)" />
      <circle cx={W - 80} cy={46} r="13" fill="var(--accent)" opacity="0.55" />
      {/* pollinators — one per biome cleared, capped */}
      {Array.from({ length: Math.min(clears, 6) }).map((_, i) => (
        <text key={i} x={90 + i * 42} y={40 + (hashString('bee' + i) % 22)} fontSize="13" opacity="0.7">{(hashString('b' + i) % 2) ? '🦋' : '🐝'}</text>
      ))}
      {/* ground */}
      <path d={`M0 ${groundY} Q${W / 2} ${groundY - 10} ${W} ${groundY} L${W} ${height} L0 ${height} Z`} fill="var(--success)" opacity="0.13" />
      <path d={`M0 ${groundY} Q${W / 2} ${groundY - 10} ${W} ${groundY}`} stroke="var(--success)" strokeWidth="1.5" opacity="0.4" fill="none" />
      {/* empty plots hint */}
      {count === 0 && Array.from({ length: 7 }).map((_, i) => (
        <g key={i} opacity="0.4">
          <ellipse cx={70 + i * 95} cy={groundY + 6} rx="11" ry="4" fill="none" stroke="var(--dim)" strokeDasharray="2 3" />
        </g>
      ))}
      {placed.map((p, i) => <Plant key={i} x={p.x} groundY={p.y} seed={p.seed} scale={p.scale} opacity={p.opacity} language={language} />)}
      {count > 0 && emptySlots > 0 && Array.from({ length: emptySlots }).map((_, i) => (
        <ellipse key={'e' + i} cx={W - 70 - i * 60} cy={groundY + 8} rx="9" ry="3.5" fill="none" stroke="var(--dim)" strokeDasharray="2 3" opacity="0.35" />
      ))}
    </svg>
  )
}

// ── Compact metric strip ─────────────────────────────────────────────────────
// Compact inline metric strip — a dense single row instead of bulky number cards.
function StatLine({ items, style }: { items: { label: string; value: React.ReactNode; color?: string }[]; style?: React.CSSProperties }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px 18px', padding: '10px 14px', borderRadius: 10, background: C.surf, border: `1px solid ${C.border}`, ...style }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: it.color || C.text, letterSpacing: '-0.3px' }}>{it.value}</span>
          <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, letterSpacing: '0.05em' }}>{it.label}</span>
        </span>
      ))}
    </div>
  )
}

// ══ STORY: Hush / restoration meter ═══════════════════════════════════════════
function HushMeter({ state, height = 8 }: { state: GardenState; height?: number }) {
  const r = restorationPct(state)
  return (
    <div style={{ position: 'relative', height, borderRadius: height / 2, overflow: 'hidden', background: 'var(--dim)', opacity: 0.9 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(45deg, var(--surf2), var(--surf2) 4px, var(--surf3) 4px, var(--surf3) 8px)' }} />
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${r}%`, background: 'linear-gradient(90deg, var(--success), var(--accent))', borderRadius: height / 2, transition: 'width 0.6s ease' }} />
    </div>
  )
}

function StoryStrip({ state, onOpen }: { state: GardenState; onOpen: () => void }) {
  const act = currentAct(state)
  const obj = actObjective(state)
  const r = restorationPct(state)
  return (
    <button onClick={onOpen} style={{
      display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
      borderRadius: 14, border: `1px solid ${C.border}`, background: C.surf, padding: '16px 18px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em' }}>📖 STORY</span>
          <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em' }}>ACT {act.roman}</span>
          <span style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: '-0.4px' }}>{act.title}</span>
        </div>
        <span title="How much of the world you've restored by answering correctly. Push it to 0% to break the Hush." style={{ fontFamily: AF.m, fontSize: 11, color: r >= 100 ? 'var(--success)' : C.muted }}>
          {r >= 100 ? 'Verdant restored' : `Hush ${hushPct(state)}% · ${r}% restored`}
        </span>
      </div>
      <HushMeter state={state} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: AF.b, fontSize: 12.5, color: C.muted, flex: 1, minWidth: 200 }}>{COMPANION.glyph} {obj.label}</span>
        <span style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>Read the Chronicle →</span>
      </div>
    </button>
  )
}

// ══ HUB / GREENHOUSE ══════════════════════════════════════════════════════════
function Hub({ state, langCode, onStartExpedition, onOpenShop, onReset, onOpenChronicle }: {
  state: GardenState; langCode: string
  onStartExpedition: () => void; onOpenShop: () => void; onReset: () => void; onOpenChronicle: () => void
}) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '48px'
  const specimens = masteredSpecimens(state).sort((a, b) => b.bornAt - a.bornAt)
  const lvl = gardenLevel(state)
  const totalClears = Object.values(state.clears).reduce((a, b) => a + (b ?? 0), 0)
  const supported = supportedFor(langCode)
  const avail = availableBiomes(state, supported)
  const unlockedCount = avail.filter(b => isBiomeUnlocked(state, b.id)).length

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: `24px ${hp} 64px` }}>
        {/* Story */}
        <StoryStrip state={state} onOpen={onOpenChronicle} />
        {/* Garden viz */}
        <div style={{ borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.surf, marginBottom: 18 }}>
          <div style={{ position: 'relative' }}>
            <MotifWatermark language={langCodeToName(langCode)} opacity={0.05} />
            <GardenPlot specimens={specimens} clears={totalClears} height={isMobile ? 180 : 230} language={langCodeToName(langCode)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, padding: '14px 18px', borderTop: `1px solid ${C.border}` }}>
            <div>
              <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 3 }}>GARDEN · LEVEL {lvl.level}</div>
              <div style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px' }}>{lvl.title}</div>
            </div>
            <div style={{ flex: 1, minWidth: 160, maxWidth: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: AF.b, fontSize: 11, color: C.muted, marginBottom: 5 }}>
                <span>{specimens.length} specimen{specimens.length !== 1 ? 's' : ''}</span>
                <span>{lvl.toNext} to next</span>
              </div>
              <div style={{ height: 6, background: C.surf2, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${lvl.need > 0 ? Math.min(100, (lvl.have / lvl.need) * 100) : 100}%`, background: 'var(--success)', borderRadius: 3, transition: 'width 0.5s' }} />
              </div>
            </div>
            <PollenPill amount={state.pollen} />
          </div>
        </div>

        {/* Primary actions */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={onStartExpedition} style={{
            flex: 2, minWidth: 220, fontFamily: AF.b, fontSize: 16, fontWeight: 700,
            background: 'var(--accent)', color: C.bg, border: 'none', padding: '18px 24px',
            borderRadius: 12, cursor: 'pointer', textAlign: 'left', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🧭</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Begin Expedition <Kbd>E</Kbd></div>
                <div style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 400, opacity: 0.85, marginTop: 2 }}>Brave a biome · bank Pollen · grow your garden</div>
              </div>
            </div>
          </button>
          <button onClick={onOpenShop} style={{
            flex: 1, minWidth: 160, fontFamily: AF.b, fontSize: 15, fontWeight: 600,
            background: C.surf, color: C.text, border: `1px solid ${C.border2}`, padding: '18px 22px',
            borderRadius: 12, cursor: 'pointer', textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🏡</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Greenhouse <Kbd>G</Kbd></div>
                <div style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 400, color: C.muted, marginTop: 2 }}>Spend Pollen on permanent unlocks</div>
              </div>
            </div>
          </button>
        </div>

        {/* Stats */}
        <StatLine style={{ marginBottom: 22 }} items={[
          { label: 'expeditions', value: state.stats.runs },
          { label: 'trials won', value: state.stats.bosses ?? 0 },
          { label: 'biomes', value: `${unlockedCount}/${avail.length}` },
          { label: '🪵 heartwood', value: (state.heartwood ?? 0).toLocaleString(), color: 'var(--accent)' },
          { label: 'lifetime 🌸', value: state.stats.totalPollen.toLocaleString() },
        ]} />
        {state.stats.withered > 0 && (
          <div style={{ fontFamily: AF.b, fontSize: 11, color: C.dim, margin: '-14px 0 22px', paddingLeft: 2 }}>{state.stats.withered} withered · max depth {Math.max(0, ...Object.values(state.depth).map(d => d ?? 0), 0)}</div>
        )}

        {/* Field journal */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em' }}>FIELD JOURNAL · {specimens.length} SPECIMENS</div>
          <button onClick={onReset} style={{ fontFamily: AF.b, fontSize: 11, color: C.dim, background: 'transparent', border: 'none', cursor: 'pointer' }}>Reset garden</button>
        </div>
        {specimens.length === 0 ? (
          <div style={{ padding: '28px 24px', borderRadius: 12, border: `1px dashed ${C.border2}`, background: C.surf, textAlign: 'center' }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>🌱</div>
            <div style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>Your soil is bare. Master a verb on an expedition to grow your first specimen.</div>
            <div style={{ fontFamily: AF.b, fontSize: 12, color: C.dim, marginTop: 6 }}>A verb becomes a specimen after {MASTERY_THRESHOLD} correct answers in the wilds.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 8 }}>
            {specimens.map(s => {
              const b = biomeById(s.biome)
              return (
                <div key={s.verb} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9, background: C.surf, border: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 18 }}>{b.glyph}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: AF.m, fontSize: 14, color: C.text, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.verb}</div>
                    <div style={{ fontFamily: AF.b, fontSize: 10, color: C.dim }}>{b.name}</div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ══ GREENHOUSE SHOP ═══════════════════════════════════════════════════════════
type ShopItem = { id: string; name: string; desc: string; glyph: string; cost: number; owned: boolean; locked?: string; currency?: 'pollen' | 'heartwood'; rarity?: TraitRarity }

function buildShop(state: GardenState, langCode: string): { group: string; items: ShopItem[] }[] {
  const supported = supportedFor(langCode)
  const avail = availableBiomes(state, supported)

  const vitTier = VITALITY_TIERS.indexOf(state.maxVitality)
  const vitItems: ShopItem[] = []
  if (vitTier >= 0 && vitTier < VITALITY_TIERS.length - 1) {
    vitItems.push({
      id: 'vitality', glyph: '💚', name: `Hardier Stock → ${VITALITY_TIERS[vitTier + 1]} vitality`,
      desc: 'Begin every expedition with one more point of vitality.',
      cost: VITALITY_COSTS[vitTier + 1], owned: false,
    })
  } else {
    vitItems.push({ id: 'vitality', glyph: '💚', name: `Hardiest Stock · ${state.maxVitality} vitality`, desc: 'Maxed out — your stock is as hardy as it gets.', cost: 0, owned: true })
  }

  const compTier = COMPOST_TIERS.findIndex(t => Math.abs(t - state.pollenMultiplier) < 0.001)
  const compItems: ShopItem[] = []
  if (compTier >= 0 && compTier < COMPOST_TIERS.length - 1) {
    compItems.push({
      id: 'compost', glyph: '♻️', name: `Compost Bin → +${Math.round((COMPOST_TIERS[compTier + 1] - 1) * 100)}% Pollen`,
      desc: 'Richer soil — every expedition banks more Pollen.',
      cost: COMPOST_COSTS[compTier + 1], owned: false,
    })
  } else {
    compItems.push({ id: 'compost', glyph: '♻️', name: `Compost · +${Math.round((state.pollenMultiplier - 1) * 100)}% Pollen`, desc: 'Maxed out — your soil is perfectly rich.', cost: 0, owned: true })
  }

  const biomeItems: ShopItem[] = avail.filter(b => b.cost > 0).map(b => ({
    id: 'biome:' + b.id, glyph: b.glyph, name: `Unlock ${b.name}`,
    desc: b.flavor, cost: b.cost, owned: isBiomeUnlocked(state, b.id),
  }))

  const rorder = { common: 0, rare: 1, legendary: 2 }
  const traitItems: ShopItem[] = TRAITS.filter(t => t.cost > 0)
    .sort((a, b) => rorder[a.rarity] - rorder[b.rarity])
    .map(t => ({
      id: 'trait:' + t.id, glyph: t.glyph, name: `Cultivate ${t.name}`,
      desc: t.blurb + ' Adds it to your draft pool.', cost: t.cost,
      owned: state.unlockedTraits.includes(t.id), rarity: t.rarity,
    }))

  const ogItems: ShopItem[] = OLD_GROWTH.map(it => {
    const owned = state.oldGrowth[it.id] ?? 0
    const maxed = it.maxTier != null && owned >= it.maxTier
    return {
      id: 'og:' + it.id, glyph: it.glyph, currency: 'heartwood' as const,
      name: maxed ? `${it.name} · maxed` : `${it.name}${owned > 0 ? ` · tier ${owned + 1}` : ''}`,
      desc: it.desc + (owned > 0 ? ` (owned ×${owned})` : ''),
      cost: maxed ? 0 : oldGrowthCost(it, owned), owned: maxed,
    }
  })

  return [
    { group: 'BIOMES', items: biomeItems },
    { group: 'ADAPTATIONS', items: traitItems },
    { group: 'HOMESTEAD', items: [...vitItems, ...compItems] },
    { group: 'OLD GROWTH · spend 🪵 Heartwood', items: ogItems },
  ].filter(g => g.items.length > 0)
}

function Shop({ state, langCode, onBuy, onBack }: { state: GardenState; langCode: string; onBuy: (item: ShopItem) => void; onBack: () => void }) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '48px'
  const groups = buildShop(state, langCode)
  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: `24px ${hp} 64px` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <h2 style={{ fontFamily: AF.h, fontSize: 24, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.7px' }}>Greenhouse</h2>
            <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: '4px 0 0' }}>Permanent upgrades. They persist across every expedition.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {state.heartwood > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, background: C.surf2, border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13 }}>🪵</span>
                <span style={{ fontFamily: AF.m, fontSize: 14, fontWeight: 700, color: C.text }}>{state.heartwood}</span>
              </div>
            )}
            <PollenPill amount={state.pollen} />
          </div>
        </div>
        {groups.map(g => (
          <div key={g.group} style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 10 }}>{g.group}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {g.items.map(item => {
                const hw = item.currency === 'heartwood'
                const afford = (hw ? state.heartwood : state.pollen) >= item.cost
                const rm = item.rarity ? TRAIT_RARITY_META[item.rarity] : null
                return (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 11, background: C.surf, border: `1px solid ${item.owned ? C.greenBorder : rm && item.rarity !== 'common' ? rm.color : C.border}` }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{item.glyph}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: '-0.3px' }}>{item.name}</span>
                        {rm && item.rarity !== 'common' && <span style={{ fontFamily: AF.m, fontSize: 8.5, fontWeight: 700, color: rm.color, border: `1px solid ${rm.color}`, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.06em' }}>{rm.label.toUpperCase()}</span>}
                      </div>
                      <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 2 }}>{item.desc}</div>
                    </div>
                    {item.owned ? (
                      <span style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: 'var(--success)', flexShrink: 0, padding: '8px 14px' }}>✓ Owned</span>
                    ) : (
                      <button onClick={() => afford && onBuy(item)} disabled={!afford} style={{
                        flexShrink: 0, fontFamily: AF.b, fontSize: 13, fontWeight: 700,
                        background: afford ? (hw ? C.surf2 : 'var(--accent)') : C.surf2, color: afford ? (hw ? C.text : C.bg) : C.dim,
                        border: hw && afford ? `1px solid ${C.border2}` : 'none', padding: '9px 16px', borderRadius: 8, cursor: afford ? 'pointer' : 'not-allowed',
                        display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                      }}>{hw ? '🪵' : '🌸'} {item.cost.toLocaleString()}</button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
        <button onClick={onBack} style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '11px 22px', borderRadius: 9, cursor: 'pointer' }}>← Back to garden</button>
      </div>
    </div>
  )
}

// ══ EXPEDITION SETUP (biome + trait draft) ════════════════════════════════════
function Setup({ state, langCode, loading, onLaunch, onBack }: {
  state: GardenState; langCode: string; loading: boolean
  onLaunch: (biome: Biome, traitId: TraitId, depth: number) => void; onBack: () => void
}) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '48px'
  const supported = supportedFor(langCode)
  const avail = availableBiomes(state, supported)
  const [biomeId, setBiomeId] = useState<BiomeId | null>(null)
  const [depth, setDepth] = useState(0)
  // Offer 3 of the unlocked traits, weighted so rarer ones appear less often.
  const draftPool = useMemo(() => {
    const pool = state.unlockedTraits.map(traitById)
    const weight = (t: Trait) => t.rarity === 'legendary' ? 1 : t.rarity === 'rare' ? 3 : 6
    const picked: TraitId[] = []
    const rest = [...pool]
    while (picked.length < 3 && rest.length) {
      const total = rest.reduce((s, t) => s + weight(t), 0)
      let r = Math.random() * total
      let idx = 0
      for (let i = 0; i < rest.length; i++) { r -= weight(rest[i]); if (r <= 0) { idx = i; break } }
      picked.push(rest[idx].id); rest.splice(idx, 1)
    }
    return picked
  }, [state.unlockedTraits, biomeId])
  const [traitId, setTraitId] = useState<TraitId | null>(null)

  useEffect(() => { if (draftPool.length) setTraitId(draftPool[0]) }, [draftPool])
  const maxDepth = biomeId ? Math.min(MAX_DEPTH, (state.depth[biomeId] ?? 0) + 1) : 0
  useEffect(() => { setDepth(d => Math.min(d, maxDepth)) }, [maxDepth])

  const biome = biomeId ? biomeById(biomeId) : null
  const cfg = depthConfig(depth)
  const canLaunch = biome && isBiomeUnlocked(state, biome.id) && traitId && !loading

  // Setup keyboard: Enter launches, ←/→ set depth, 1–3 draft a trait, Esc cancels.
  const avMemo = avail
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === 'Enter' && canLaunch && biome && traitId) { e.preventDefault(); onLaunch(biome, traitId, depth); return }
      if (e.key === 'ArrowLeft') { e.preventDefault(); setDepth(d => Math.max(0, d - 1)) }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setDepth(d => Math.min(maxDepth, d + 1)) }
      else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault()
        const idx = avMemo.findIndex(b => b.id === biomeId)
        let next = idx + (e.key === 'ArrowDown' ? 1 : -1)
        for (let n = 0; n < avMemo.length; n++) {
          const cand = avMemo[(next + avMemo.length * 2) % avMemo.length]
          if (isBiomeUnlocked(state, cand.id)) { setBiomeId(cand.id); break }
          next += (e.key === 'ArrowDown' ? 1 : -1)
        }
      } else {
        const ti = ['1', '2', '3'].indexOf(e.key)
        if (ti >= 0 && draftPool[ti]) { e.preventDefault(); setTraitId(draftPool[ti]) }
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [canLaunch, biome, traitId, depth, maxDepth, draftPool, biomeId, avMemo, state, onLaunch])

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: `24px ${hp} 80px` }}>
        <h2 style={{ fontFamily: AF.h, fontSize: 24, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.7px' }}>Plan your expedition</h2>
        <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: '4px 0 24px' }}>{NORMAL_GROVES} groves, then a timed <strong style={{ color: C.text }}>Conjugation Trial</strong>. Draft one adaptation and set your depth.</p>

        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 10 }}>1 · CHOOSE A BIOME</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
          {avail.map(b => {
            const unlocked = isBiomeUnlocked(state, b.id)
            const active = biomeId === b.id
            const cleared = state.clears[b.id] ?? 0
            return (
              <button key={b.id} onClick={() => unlocked && setBiomeId(b.id)} disabled={!unlocked} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 11, textAlign: 'left',
                border: `1px solid ${active ? 'var(--accent)' : C.border}`, background: active ? C.accentBg : C.surf,
                cursor: unlocked ? 'pointer' : 'not-allowed', opacity: unlocked ? 1 : 0.55,
              }}>
                <span style={{ fontSize: 24, flexShrink: 0 }}>{b.glyph}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: active ? 'var(--accent)' : C.text, letterSpacing: '-0.3px' }}>{b.name}</span>
                    {cleared > 0 && <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--success)' }}>bloomed ×{cleared}</span>}
                  </div>
                  <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 2 }}>{b.flavor}</div>
                  <div style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, marginTop: 4 }}>{b.tenses.filter(t => supported.has(t)).join(' · ').replace(/_/g, ' ')}</div>
                </div>
                {!unlocked && <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px', whiteSpace: 'nowrap' }}>🌸 {b.cost} · locked</span>}
                {active && <span style={{ color: 'var(--accent)', fontSize: 16 }}>✓</span>}
              </button>
            )
          })}
        </div>

        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 10 }}>2 · DRAFT AN ADAPTATION</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 8, marginBottom: 30 }}>
          {draftPool.map((id, i) => {
            const t = traitById(id)
            const active = traitId === id
            const rm = TRAIT_RARITY_META[t.rarity]
            return (
              <button key={id} onClick={() => setTraitId(id)} style={{
                padding: '16px', borderRadius: 11, textAlign: 'left', cursor: 'pointer',
                border: `1px solid ${active ? rm.color : C.border}`, background: active ? rm.bg : C.surf,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 22 }}>{t.glyph}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t.rarity !== 'common' && <span style={{ fontFamily: AF.m, fontSize: 9, fontWeight: 700, color: rm.color, letterSpacing: '0.06em' }}>{rm.label.toUpperCase()}</span>}
                    <Kbd>{String(i + 1)}</Kbd>
                  </div>
                </div>
                <div style={{ fontFamily: AF.h, fontSize: 14, fontWeight: 700, color: active ? rm.color : C.text, marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, lineHeight: 1.45 }}>{t.blurb}</div>
              </button>
            )
          })}
        </div>

        {/* 3 · Depth of the Hush */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em' }}>3 · DEPTH OF THE HUSH <Kbd>← →</Kbd></div>
          <div style={{ fontFamily: AF.m, fontSize: 11, color: depth > 0 ? 'var(--accent)' : C.dim }}>
            {depth === 0 ? 'Surface' : `Depth ${depth}`} · ×{cfg.sporeMult.toFixed(1)} Pollen{cfg.heartwood ? ' · 🪵 Heartwood' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {Array.from({ length: maxDepth + 1 }).map((_, d) => (
            <button key={d} onClick={() => setDepth(d)} disabled={!biomeId} style={{
              fontFamily: AF.m, fontSize: 13, fontWeight: depth === d ? 700 : 400, padding: '7px 14px', borderRadius: 8, cursor: biomeId ? 'pointer' : 'not-allowed',
              border: `1px solid ${depth === d ? 'var(--accent)' : C.border}`, background: depth === d ? C.accentBg : 'transparent', color: depth === d ? 'var(--accent)' : C.muted,
            }}>{d === 0 ? 'Surface' : `D${d}`}</button>
          ))}
        </div>
        <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginBottom: 28 }}>
          {depth === 0 ? 'A fair trial. Best for learning a biome.' : `−${Math.floor(depth / 2)} starting vitality · Trial timer ${cfg.bossTimer}s · ${Math.round(cfg.irregularBias * 100)}% irregular bias${cfg.missPenaltyBonus ? ' · misses bite harder' : ''}.`}
          {biomeId && maxDepth === (state.depth[biomeId] ?? 0) + 1 && depth === maxDepth && <span style={{ color: 'var(--accent)' }}> Clear it to unlock the next depth.</span>}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={() => canLaunch && biome && traitId && onLaunch(biome, traitId, depth)} disabled={!canLaunch} style={{
            display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: AF.b, fontSize: 15, fontWeight: 700,
            background: canLaunch ? 'var(--accent)' : C.surf2, color: canLaunch ? C.bg : C.dim,
            border: 'none', padding: '14px 30px', borderRadius: 10, cursor: canLaunch ? 'pointer' : 'not-allowed',
          }}>{loading ? 'Preparing trail…' : biome ? `Enter ${biome.name}${depth > 0 ? ` · Depth ${depth}` : ''} →` : 'Choose a biome'}{canLaunch && <Kbd>↵</Kbd>}</button>
          <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: AF.b, fontSize: 14, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '14px 22px', borderRadius: 10, cursor: 'pointer' }}>Cancel <Kbd>Esc</Kbd></button>
        </div>
      </div>
    </div>
  )
}

// ══ RUN ENGINE ════════════════════════════════════════════════════════════════
type RunCard = DrillCardData & { mode: 'type' | 'quiz'; options?: string[]; elite?: boolean }

type ChoiceOption =
  | { kind: 'trait'; traitId: TraitId; label: string; desc: string; glyph: string }
  | { kind: 'heal'; amount: number; label: string; desc: string; glyph: string }
  | { kind: 'pollen'; amount: number; label: string; desc: string; glyph: string }
  | { kind: 'overgrowth'; label: string; desc: string; glyph: string }
  | { kind: 'bloom'; amount: number; label: string; desc: string; glyph: string }
  | { kind: 'shrine'; traitId: TraitId; label: string; desc: string; glyph: string }
  | { kind: 'spring'; label: string; desc: string; glyph: string }

type LastResult = { correct: boolean; card: RunCard; gained: number; vitLost: number; grew: string | null; crit?: boolean } | null

type Encounter = { verb: string; rarity: Rarity; meaning: string; verbType: string }

// ── The run is a branching map of nodes (Phase 5) ────────────────────────────
// Each column offers a choice of paths; fight nodes hold cards, event nodes a
// boon, and the final column is the boss Trial.
type NodeKind = 'thicket' | 'warren' | 'glade' | 'spring' | 'boss'
type RunNode = { id: string; kind: NodeKind; label: string; glyph: string; desc: string; cards?: RunCard[] }
const NODE_IS_FIGHT = (k: NodeKind) => k === 'thicket' || k === 'warren' || k === 'boss'

type RunState = {
  status: 'select' | 'card' | 'feedback' | 'choice' | 'won' | 'lost'
  biome: Biome
  langCode: string
  depth: number
  map: RunNode[][]                       // columns; each is a 1–2 node choice
  mapPos: number                         // current column index
  current: RunNode | null                // the node being played / resolved
  traits: TraitId[]
  vitality: number
  maxVitality: number
  combo: number
  bestCombo: number
  comboCap: number
  spores: number
  sporeMult: number
  missPenalty: number                    // extra vitality lost per miss (from depth)
  cardIdx: number
  last: LastResult
  deepRootUsed: boolean
  photoCounter: number
  critCounter: number                    // apex bloom counter
  groveMutated: boolean
  pendingMutation: boolean
  timer: number | null                   // boss countdown (seconds), null outside boss
  bossTimerMax: number
  rerolls: number                        // adaptation re-draws available
  runCorrect: Record<string, number>     // per-verb correct this run
  baseline: Record<string, number>       // per-verb correct already banked
  newlyMastered: string[]
  reviewHits: string[]                   // weak/elite verbs answered correctly
  choices: ChoiceOption[] | null
  seenDiscovered: Record<string, true>   // verbs already in the Verbarium
  discoveries: Record<string, Rarity>    // verbs newly discovered this run
  pendingEncounter: Encounter | null     // a wild word awaiting reveal
}

type RunAction =
  | { type: 'START'; payload: { biome: Biome; langCode: string; traits: TraitId[]; maxVitality: number; map: RunNode[][]; baseline: Record<string, number>; discoveredVerbs: string[]; depth: number; comboCap: number; rerolls: number } }
  | { type: 'SELECT'; node: RunNode }
  | { type: 'ANSWER'; correct: boolean }
  | { type: 'NEXT' }
  | { type: 'CHOOSE'; option: ChoiceOption }
  | { type: 'REVEAL' }
  | { type: 'TICK' }
  | { type: 'REROLL' }
  | { type: 'RESTORE'; payload: RunState }

// ── Active-run persistence (so a mid-expedition refresh doesn't lose the run) ──
const RUN_KEY = (lang: string) => `c8-conjugarden-run-v1:${lang}`
function readRun(lang: string): RunState | null {
  if (typeof window === 'undefined') return null
  try { const r = window.localStorage.getItem(RUN_KEY(lang)); return r ? (JSON.parse(r) as RunState) : null } catch { return null }
}
function writeRun(lang: string, run: RunState): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(RUN_KEY(lang), JSON.stringify(run)) } catch {}
}
function clearRun(lang: string): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(RUN_KEY(lang)) } catch {}
}

// Pollen banked from a run, after stakes + Old-Growth bonuses.
// Win = full harvest, retreat (flee) = 60%, wither = forfeit the unbanked harvest.
function bankedPollen(r: RunState, g: GardenState): number {
  const richsoil = g.oldGrowth['richsoil'] ?? 0
  const pollenMult = g.pollenMultiplier * (1 + 0.1 * richsoil)
  const kept = r.status === 'won' ? r.spores : r.status === 'lost' ? 0 : Math.round(r.spores * 0.6)
  return Math.round(kept * pollenMult)
}

// A wild word is any card whose verb isn't in the Verbarium yet (this run or prior).
function encounterFor(state: RunState, card: RunCard | undefined): Encounter | null {
  if (!card) return null
  const v = card.verb
  if (state.seenDiscovered[v] || state.discoveries[v]) return null
  return { verb: v, rarity: rarityOf(v, card.verbType, state.langCode), meaning: card.translation, verbType: card.verbType }
}

// Draft a trait the run doesn't hold, weighting toward deeper columns giving rarer picks.
function pickDraftTrait(state: RunState, allowRareOnly = false): TraitId | null {
  let pool = TRAITS.filter(t => !state.traits.includes(t.id))
  if (allowRareOnly) pool = pool.filter(t => t.rarity !== 'common')
  if (!pool.length) return null
  const weight = (t: Trait) => {
    const base = t.rarity === 'legendary' ? 1 : t.rarity === 'rare' ? 3 : 6
    const depthLift = t.rarity === 'common' ? 1 : 1 + Math.max(0, state.mapPos) * 0.5
    return base * depthLift
  }
  const total = pool.reduce((s, t) => s + weight(t), 0)
  let r = Math.random() * total
  for (const t of pool) { r -= weight(t); if (r <= 0) return t.id }
  return pool[0].id
}

// Boons offered at a Glade event node.
function makeEventOptions(state: RunState): ChoiceOption[] {
  const opts: ChoiceOption[] = []
  if (state.traits.length < 5) {
    const tid = pickDraftTrait(state)
    if (tid) { const t = traitById(tid); opts.push({ kind: 'trait', traitId: t.id, label: t.name, desc: t.blurb, glyph: t.glyph }) }
  }
  if (state.vitality < state.maxVitality) opts.push({ kind: 'heal', amount: 1, label: 'Morning Dew', desc: 'Restore 1 vitality.', glyph: '💧' })
  else opts.push({ kind: 'pollen', amount: 60, label: 'Pollen Cache', desc: '+60 spores, banked now.', glyph: '🌸' })
  opts.push({ kind: 'pollen', amount: 45, label: 'Nectar Pocket', desc: '+45 spores.', glyph: '🍯' })
  opts.push({ kind: 'overgrowth', label: 'Overgrowth', desc: 'The next fight pays +50% spores, but every miss costs +1 vitality.', glyph: '🌶️' })
  opts.push({ kind: 'bloom', amount: 8, label: 'Wild Bloom', desc: 'Set your bloom streak to 8 instantly.', glyph: '🌻' })
  if (state.vitality > 1 && state.traits.length < 5) {
    const tid = pickDraftTrait(state, true)
    if (tid) { const t = traitById(tid); opts.push({ kind: 'shrine', traitId: t.id, label: 'Withered Shrine', desc: `Offer 1 vitality to claim ${t.name} (${TRAIT_RARITY_META[t.rarity].label}).`, glyph: '⛩️' }) }
  }
  const traitOpt = opts.find(o => o.kind === 'trait')
  const rest = opts.filter(o => o.kind !== 'trait').sort(() => Math.random() - 0.5)
  const chosen: ChoiceOption[] = traitOpt ? [traitOpt] : []
  for (const o of rest) { if (chosen.length >= 3) break; chosen.push(o) }
  return chosen.slice(0, 3)
}

// Enter a node: fight nodes start their cards; event nodes present boons.
function enterNode(state: RunState, pos: number, node: RunNode): RunState {
  const base: RunState = { ...state, mapPos: pos, current: node, last: null, pendingEncounter: null, choices: null }
  if (NODE_IS_FIGHT(node.kind)) {
    const perennial = state.traits.includes('perennial')
    const next: RunState = {
      ...base, status: 'card', cardIdx: 0,
      combo: perennial ? Math.max(state.combo, 3) : state.combo,
      deepRootUsed: false,
      groveMutated: state.pendingMutation, pendingMutation: false,
      timer: node.kind === 'boss' ? state.bossTimerMax : null,
    }
    next.pendingEncounter = encounterFor(next, node.cards?.[0])
    return next
  }
  const options: ChoiceOption[] = node.kind === 'spring'
    ? [{ kind: 'spring', label: 'Drink deep', desc: 'Heal to full and raise max vitality by 1.', glyph: '🪞' }]
    : makeEventOptions(state)
  return { ...base, status: 'choice', choices: options }
}

// Advance to the next column: auto-enter a lone node (the boss), else offer a path choice.
function advanceColumn(state: RunState): RunState {
  const pos = state.mapPos + 1
  if (pos >= state.map.length) return { ...state, status: 'won' }
  const col = state.map[pos]
  if (col.length === 1) return enterNode(state, pos, col[0])
  return { ...state, mapPos: pos, current: null, status: 'select', last: null, pendingEncounter: null, choices: null }
}

function runReducer(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case 'START': {
      const { biome, langCode, traits, maxVitality, map, baseline, discoveredVerbs, depth, comboCap, rerolls } = action.payload
      const cfg = depthConfig(depth)
      const maxV = maxVitality + (traits.includes('evergreen') ? 2 : 0)
      const vitality = Math.max(1, maxV + (traits.includes('resilientbark') ? 1 : 0) + cfg.startVitalityDelta)
      const base: RunState = {
        status: 'select', biome, langCode, depth, map, mapPos: -1, current: null, traits,
        maxVitality: maxV, vitality,
        combo: 0, bestCombo: 0, comboCap,
        spores: 0, sporeMult: cfg.sporeMult, missPenalty: cfg.missPenaltyBonus,
        cardIdx: 0, last: null,
        deepRootUsed: false, photoCounter: 0, critCounter: 0,
        groveMutated: false, pendingMutation: false,
        timer: null, bossTimerMax: cfg.bossTimer, rerolls,
        runCorrect: {}, baseline, newlyMastered: [], reviewHits: [], choices: null,
        seenDiscovered: Object.fromEntries(discoveredVerbs.map(v => [v, true as const])),
        discoveries: {}, pendingEncounter: null,
      }
      return advanceColumn(base)
    }
    case 'SELECT':
      return enterNode(state, state.mapPos, action.node)
    case 'ANSWER': {
      const card = state.current!.cards![state.cardIdx]
      const isIrregular = card.verbType === 'Irregular'
      const t = (id: TraitId) => state.traits.includes(id)
      if (action.correct) {
        const newCombo = state.combo + 1
        const mult = comboMultiplier(newCombo, state.comboCap + (t('bloomcaller') ? 1 : 0))
        let gain = BASE_SPORES
        if (t('pollinator')) gain += 5
        if (t('symbiosis')) gain += 3 * Math.max(0, state.traits.length - 1)
        if (t('nitrogen') && !isIrregular) gain *= 1.6
        if (t('thornback') && isIrregular) gain *= 2.2
        if (card.elite) gain *= 2
        gain = gain * mult
        if (state.groveMutated) gain *= 1.5
        gain *= state.sporeMult
        // apex bloom: every 3rd correct crits
        const critCounter = state.critCounter + 1
        let crit = false
        if (t('apex') && critCounter % 3 === 0) { gain *= 2; crit = true }
        gain = Math.round(gain)

        // mastery
        const runCorrect = { ...state.runCorrect, [card.verb]: (state.runCorrect[card.verb] ?? 0) + 1 }
        const totalCorrect = (state.baseline[card.verb] ?? 0) + runCorrect[card.verb]
        const alreadyBanked = (state.baseline[card.verb] ?? 0) >= MASTERY_THRESHOLD
        let grew: string | null = null
        let newlyMastered = state.newlyMastered
        let vitality = state.vitality
        if (!alreadyBanked && totalCorrect === MASTERY_THRESHOLD && !state.newlyMastered.includes(card.verb)) {
          grew = card.verb
          newlyMastered = [...state.newlyMastered, card.verb]
          gain += MASTERY_BONUS
          if (t('seedbank')) vitality = Math.min(state.maxVitality, vitality + 1)
        }

        // photosynthesis
        let photoCounter = state.photoCounter
        if (t('photosynthesis')) {
          photoCounter += 1
          if (photoCounter >= 8) { vitality = Math.min(state.maxVitality, vitality + 1); photoCounter -= 8 }
        }

        // boss timer: a correct answer buys 3 seconds
        const timer = state.timer == null ? null : Math.min(state.bossTimerMax, state.timer + 3)
        // review tracking for elite (weak/due) verbs
        const reviewHits = card.elite && !state.reviewHits.includes(card.verb) ? [...state.reviewHits, card.verb] : state.reviewHits

        return {
          ...state, status: 'feedback', combo: newCombo, bestCombo: Math.max(state.bestCombo, newCombo),
          spores: state.spores + gain, vitality, photoCounter, critCounter, timer, runCorrect, newlyMastered, reviewHits,
          last: { correct: true, card, gained: gain, vitLost: 0, grew, crit },
        }
      } else {
        let loss = 1 + state.missPenalty
        if (t('thornback') && isIrregular) loss += 1
        if (state.groveMutated) loss += 1
        let deepRootUsed = state.deepRootUsed
        if (t('deeproots') && !deepRootUsed) { loss = 0; deepRootUsed = true }
        const vitality = state.vitality - loss
        const combo = t('mycelium') ? Math.floor(state.combo / 2) : 0
        // compost heart: salvage half spores even on a miss
        let composted = 0
        if (t('compostheart')) composted = Math.round(BASE_SPORES * 0.5 * state.sporeMult)
        // boss timer: a miss costs 5 seconds
        const timer = state.timer == null ? null : Math.max(0, state.timer - 5)
        return {
          ...state, status: 'feedback', vitality, combo, deepRootUsed, timer, spores: state.spores + composted,
          last: { correct: false, card, gained: composted, vitLost: loss, grew: null },
        }
      }
    }
    case 'NEXT': {
      if (state.vitality <= 0 || (state.timer != null && state.timer <= 0)) return { ...state, status: 'lost' }
      const cards = state.current?.cards ?? []
      if (state.cardIdx + 1 < cards.length) {
        return { ...state, status: 'card', cardIdx: state.cardIdx + 1, last: null, pendingEncounter: encounterFor(state, cards[state.cardIdx + 1]) }
      }
      // node cleared — move to the next column of the map
      return advanceColumn(state)
    }
    case 'TICK': {
      if (state.timer == null || state.status !== 'card' || state.pendingEncounter) return state
      const tt = state.timer - 1
      if (tt <= 0) return { ...state, timer: 0, status: 'lost' }
      return { ...state, timer: tt }
    }
    case 'REROLL': {
      if (state.status !== 'choice' || state.rerolls <= 0 || state.current?.kind === 'spring') return state
      return { ...state, rerolls: state.rerolls - 1, choices: makeEventOptions(state) }
    }
    case 'REVEAL': {
      if (!state.pendingEncounter) return state
      const { verb, rarity } = state.pendingEncounter
      let bonus = RARITY_META[rarity].bonus * state.sporeMult
      if (state.traits.includes('nectarfont')) bonus *= 2
      bonus = Math.round(bonus)
      let maxVitality = state.maxVitality
      let vitality = state.vitality
      if (rarity === 'legendary' && state.traits.includes('mycorrhiza')) { maxVitality += 1; vitality += 1 }
      return { ...state, pendingEncounter: null, discoveries: { ...state.discoveries, [verb]: rarity }, spores: state.spores + bonus, maxVitality, vitality }
    }
    case 'RESTORE':
      return action.payload
    case 'CHOOSE': {
      let next = { ...state }
      const o = action.option
      if (o.kind === 'trait') next.traits = [...state.traits, o.traitId]
      else if (o.kind === 'heal') next.vitality = Math.min(state.maxVitality, state.vitality + o.amount)
      else if (o.kind === 'pollen') next.spores = state.spores + o.amount
      else if (o.kind === 'overgrowth') next.pendingMutation = true
      else if (o.kind === 'bloom') next.combo = Math.max(state.combo, o.amount)
      else if (o.kind === 'shrine') { next.traits = [...state.traits, o.traitId]; next.vitality = Math.max(1, state.vitality - 1) }
      else if (o.kind === 'spring') { next.maxVitality = state.maxVitality + 1; next.vitality = state.maxVitality + 1 }
      // event resolved — advance to the next column of the map
      return advanceColumn(next)
    }
    default:
      return state
  }
}

// quiz option builder — distractors drawn from a pool of plausible answers
function buildOptions(answer: string, pool: string[]): string[] {
  const distract = Array.from(new Set(pool.filter(a => a !== answer)))
    .sort(() => Math.random() - 0.5).slice(0, 3)
  return [...distract, answer].sort(() => Math.random() - 0.5)
}

function VitalityBar({ vitality, max }: { vitality: number; max: number }) {
  return (
    <div style={{ display: 'flex', gap: 4 }} title={`Vitality: ${vitality}/${max} — your lives this run. Hit zero and the expedition withers.`}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ fontSize: 16, filter: i < vitality ? 'none' : 'grayscale(1)', opacity: i < vitality ? 1 : 0.28, transition: 'opacity 0.3s' }}>🌿</span>
      ))}
    </div>
  )
}

// ══ WILD WORD ENCOUNTER ═══════════════════════════════════════════════════════
function EncounterOverlay({ enc, biome, onReveal, isMobile, language }: { enc: Encounter; biome: Biome; onReveal: () => void; isMobile: boolean; language: Language }) {
  const [revealed, setRevealed] = useState(false)
  const rm = RARITY_META[enc.rarity]
  const seed = hashString(enc.verb)
  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 950)
    return () => clearTimeout(t)
  }, [])
  useEffect(() => { if (revealed) sound.discover(enc.rarity) }, [revealed, enc.rarity])
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); revealed ? onReveal() : setRevealed(true) }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [revealed, onReveal])

  const flourish = enc.rarity === 'legendary' ? 'A LEGENDARY word breaks the silence!'
    : enc.rarity === 'epic' ? 'A rare word stirs in the Hush!'
    : enc.rarity === 'rare' ? 'An uncommon word surfaces…'
    : 'A wild word appears.'

  return (
    <div onClick={() => (revealed ? onReveal() : setRevealed(true))} style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: `radial-gradient(circle at 50% 42%, ${rm.bg}, var(--bg) 70%)`, cursor: 'pointer', overflow: 'hidden', position: 'relative',
    }}>
      {/* rustle particles */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, opacity: revealed ? 0.5 : 0.2, transition: 'opacity 0.6s' }}>
        {[...Array(5)].map((_, i) => (
          <span key={i} className="c8-pop" style={{ position: 'absolute', left: `${15 + (seed >> (i * 2)) % 70}%`, top: `${20 + (seed >> (i * 3)) % 60}%`, fontSize: 12 + (i % 3) * 5, opacity: 0.6, color: rm.color }}>{rm.sigil}</span>
        ))}
      </div>

      <div style={{ fontFamily: AF.m, fontSize: 11, color: rm.color, letterSpacing: '0.18em', marginBottom: 18, textTransform: 'uppercase' }}>
        {biome.glyph} {revealed ? flourish : 'Something stirs in the grey…'}
      </div>

      {/* species portrait */}
      <div style={{ width: 180, height: 150, marginBottom: 8, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: rm.color, opacity: revealed ? 0.16 : 0.05, filter: 'blur(26px)', transition: 'opacity 0.6s' }} />
        <svg viewBox="0 0 180 150" width="180" height="150" style={{ position: 'relative' }}>
          <g style={{ filter: revealed ? 'none' : 'brightness(0.12)', opacity: revealed ? 1 : 0.5, transition: 'filter 0.5s, opacity 0.5s' }}>
            <Plant x={90} groundY={132} seed={seed} scale={3.4} opacity={1} language={language} />
          </g>
        </svg>
      </div>

      <div style={{
        fontFamily: AF.h, fontWeight: 700, letterSpacing: '-1.5px', lineHeight: 1,
        fontSize: isMobile ? 44 : 60, color: revealed ? C.text : 'transparent',
        textShadow: revealed ? 'none' : `0 0 24px ${rm.color}`, filter: revealed ? 'none' : 'blur(7px)',
        transition: 'filter 0.4s, color 0.4s', marginBottom: 14,
      }}>{enc.verb}</div>

      {revealed && (
        <div className="c8-pop" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <span style={{ fontFamily: AF.m, fontSize: 11, fontWeight: 700, color: rm.color, background: rm.bg, border: `1px solid ${rm.color}`, borderRadius: 20, padding: '4px 12px', letterSpacing: '0.08em' }}>{rm.sigil} {rm.label.toUpperCase()}</span>
            <span style={{ fontFamily: AF.m, fontSize: 12, color: C.muted }}>{enc.verbType}</span>
          </div>
          <div style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, fontStyle: 'italic', maxWidth: 320, textAlign: 'center' }}>“{enc.meaning}”</div>
          <div style={{ fontFamily: AF.m, fontSize: 12, color: 'var(--accent)' }}>📒 Catalogued · +{rm.bonus} 🌸 discovery bonus</div>
          <button onClick={(e) => { e.stopPropagation(); onReveal() }} style={{ marginTop: 6, fontFamily: AF.b, fontSize: 15, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '13px 30px', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }}>
            Speak it back <Kbd>↵</Kbd>
          </button>
        </div>
      )}
      {!revealed && <div style={{ fontFamily: AF.b, fontSize: 12, color: C.dim }}>tap to reveal</div>}
    </div>
  )
}

function RunView({ state, langCode, run, dispatch, onAbort }: {
  state: GardenState; langCode: string; run: RunState; dispatch: React.Dispatch<RunAction>; onAbort: () => void
}) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '56px'
  const [input, setInput] = useState('')
  const [chosen, setChosen] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const card = run.status === 'card' || run.status === 'feedback' ? run.current?.cards?.[run.cardIdx] : null

  // submit handler — records to global progress, then dispatches.
  const submit = useCallback((correct: boolean, theCard: RunCard) => {
    recordAttempt({ timestamp: Date.now(), infinitive: theCard.verb, tense: theCard.tense as any, pronoun: theCard.pronoun, status: correct ? 'correct' : 'incorrect' })
    dispatch({ type: 'ANSWER', correct })
  }, [dispatch])

  const checkTyped = useCallback(() => {
    if (!card || run.status !== 'card' || !input.trim()) return
    const norm = (s: string) => s.trim().toLowerCase().normalize('NFC')
    submit(norm(input) === norm(card.answer), card)
  }, [card, run.status, input, submit])

  const chooseQuiz = useCallback((opt: string) => {
    if (!card || run.status !== 'card' || chosen !== null) return
    setChosen(opt)
    submit(opt === card.answer, card)
  }, [card, run.status, chosen, submit])

  const next = useCallback(() => {
    setInput(''); setChosen(null)
    dispatch({ type: 'NEXT' })
    setTimeout(() => inputRef.current?.focus(), 60)
  }, [dispatch])

  useEffect(() => { if (run.status === 'card' && card?.mode === 'type') inputRef.current?.focus() }, [run.status, run.cardIdx, run.mapPos, card?.mode])

  // keyboard for feedback + quiz + path-select + event nodes
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (run.pendingEncounter) return  // the encounter overlay owns the keyboard
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (run.status === 'feedback' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); next() }
      if (run.status === 'card' && card?.mode === 'quiz' && chosen === null) {
        const ki = ['1', '2', '3', '4'].indexOf(e.key)
        if (ki >= 0 && card.options && ki < card.options.length) chooseQuiz(card.options[ki])
      }
      if (run.status === 'select') {
        const col = run.map[run.mapPos] ?? []
        if ((e.key === '1' || e.key === 'ArrowLeft') && col[0]) { e.preventDefault(); dispatch({ type: 'SELECT', node: col[0] }) }
        if ((e.key === '2' || e.key === 'ArrowRight') && col[1]) { e.preventDefault(); dispatch({ type: 'SELECT', node: col[1] }) }
      }
      if (run.status === 'choice' && run.choices) {
        const ki = ['1', '2', '3'].indexOf(e.key)
        if (ki >= 0 && run.choices[ki]) { e.preventDefault(); dispatch({ type: 'CHOOSE', option: run.choices[ki] }) }
        if (e.key.toLowerCase() === 'r' && run.rerolls > 0 && run.current?.kind !== 'spring') { e.preventDefault(); dispatch({ type: 'REROLL' }) }
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [run.status, run.map, run.mapPos, run.choices, run.rerolls, run.current, card, chosen, next, chooseQuiz, dispatch])

  // ── Boss countdown — only ticks while answering the Trial ──
  const isBoss = run.current?.kind === 'boss'
  useEffect(() => {
    if (!isBoss || run.status !== 'card' || run.pendingEncounter) return
    const id = setInterval(() => dispatch({ type: 'TICK' }), 1000)
    return () => clearInterval(id)
  }, [isBoss, run.status, run.pendingEncounter, dispatch])

  // ── Sound cues on each answer + a Trial countdown tick ──
  const [soundOn, setSoundOn] = useState(true)
  useEffect(() => { setSoundOn(sound.isEnabled()) }, [])
  useEffect(() => {
    if (run.status !== 'feedback' || !run.last) return
    if (run.last.correct) (run.last.crit ? sound.crit() : sound.correct(run.combo))
    else sound.miss()
  }, [run.last, run.status, run.combo])
  useEffect(() => { if (isBoss && run.timer != null && run.timer <= 5 && run.timer > 0) sound.tick() }, [run.timer, isBoss])

  // ── Terminal screens ──
  if (run.status === 'won' || run.status === 'lost') {
    return <RunSummary run={run} state={state} won={run.status === 'won'} onDone={onAbort} />
  }

  // ── Wild word encounter (takes over the screen, Pokémon-style) ──
  if (run.pendingEncounter) {
    return <EncounterOverlay enc={run.pendingEncounter} biome={run.biome} isMobile={isMobile} language={langCodeToName(run.langCode)} onReveal={() => {
      recordVocabularyAttempt({ timestamp: Date.now(), term: run.pendingEncounter!.verb, status: 'skipped' })
      dispatch({ type: 'REVEAL' })
    }} />
  }

  // ── Path selection (branching map) ──
  if (run.status === 'select') {
    const col = run.map[run.mapPos] ?? []
    const bossNext = run.mapPos >= run.map.length - 2
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.bg, padding: `24px ${hp}` }}>
        <div style={{ maxWidth: 680, width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em' }}>{run.biome.glyph} TRAIL {run.mapPos + 1} OF {run.map.length - 1} · CHOOSE YOUR PATH</div>
          </div>
          <h2 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 28, fontWeight: 700, color: C.text, textAlign: 'center', margin: '0 0 6px', letterSpacing: '-0.7px' }}>Which way through the wilds?</h2>
          {bossNext && <div style={{ textAlign: 'center', fontFamily: AF.b, fontSize: 13, color: 'var(--error)', marginBottom: 8 }}>⚠️ Beyond this lies the Conjugation Trial.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 18 }}>
            {col.map((node, idx) => {
              const fight = NODE_IS_FIGHT(node.kind)
              const col2 = node.kind === 'warren' ? '#B084E9' : fight ? 'var(--accent)' : 'var(--success)'
              const tag = node.kind === 'warren' ? 'elite fight · ×2 spores' : node.kind === 'thicket' ? 'a fair fight' : node.kind === 'spring' ? 'rest · no fight' : 'a boon · no fight'
              return (
                <button key={node.id} onClick={() => dispatch({ type: 'SELECT', node })} className="c8-pop" style={{
                  padding: '24px 20px', borderRadius: 14, textAlign: 'left', cursor: 'pointer',
                  border: `1px solid ${C.border2}`, background: C.surf, transition: 'border-color 0.15s, transform 0.15s',
                  display: 'flex', flexDirection: 'column', gap: 9, minHeight: 168,
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = col2; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-3px)' }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = C.border2; (e.currentTarget as HTMLButtonElement).style.transform = 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 34 }}>{node.glyph}</span>
                    <Kbd>{idx === 0 ? '1 / ←' : '2 / →'}</Kbd>
                  </div>
                  <span style={{ fontFamily: AF.h, fontSize: 19, fontWeight: 700, color: col2 }}>{node.label}</span>
                  <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, lineHeight: 1.5, flex: 1 }}>{node.desc}</span>
                  <span style={{ fontFamily: AF.m, fontSize: 10, color: col2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{tag}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // ── Event node (Glade / Mirror Spring) ──
  if (run.status === 'choice' && run.choices) {
    const isSpring = run.current?.kind === 'spring'
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.bg, padding: `24px ${hp}` }}>
        <div style={{ maxWidth: 720, width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <div style={{ fontFamily: AF.m, fontSize: 10, color: isSpring ? 'var(--success)' : 'var(--accent)', letterSpacing: '0.14em' }}>{run.current?.glyph} {isSpring ? 'A MIRROR SPRING' : 'A GLADE IN THE WILDS'}</div>
          </div>
          <h2 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 28, fontWeight: 700, color: C.text, textAlign: 'center', margin: '0 0 24px', letterSpacing: '-0.7px' }}>{isSpring ? 'Rest a moment' : 'Claim a boon'}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(run.choices.length, 3)}, 1fr)`, gap: 10 }}>
            {run.choices.map((o, i) => (
              <button key={i} onClick={() => dispatch({ type: 'CHOOSE', option: o })} className="c8-pop" style={{
                padding: '22px 18px', borderRadius: 13, textAlign: 'left', cursor: 'pointer',
                border: `1px solid ${C.border2}`, background: C.surf, transition: 'border-color 0.15s, transform 0.15s',
                display: 'flex', flexDirection: 'column', gap: 8, minHeight: 150,
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-3px)' }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = C.border2; (e.currentTarget as HTMLButtonElement).style.transform = 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 30 }}>{o.glyph}</span>
                  {!isSpring && <Kbd>{String(i + 1)}</Kbd>}
                </div>
                <span style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 700, color: C.text }}>{o.label}</span>
                <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{o.desc}</span>
              </button>
            ))}
          </div>
          {!isSpring && run.rerolls > 0 && (
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <button onClick={() => dispatch({ type: 'REROLL' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: AF.b, fontSize: 13, fontWeight: 600, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '9px 18px', borderRadius: 8, cursor: 'pointer' }}>
                🍃 Reroll boons · {run.rerolls} left <Kbd>R</Kbd>
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!card) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}><span style={{ fontFamily: AF.b, color: C.muted }}>…</span></div>

  const fb = run.status === 'feedback' ? run.last : null
  const isCorrect = fb?.correct
  const isWrong = fb && !fb.correct
  const bg = isCorrect ? C.greenBg : isWrong ? C.redBg : C.bg

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: bg, transition: 'background 0.28s', overflow: 'hidden' }}>
      {/* HUD */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: `12px ${hp} 0`, flexWrap: 'wrap' }}>
        <VitalityBar vitality={run.vitality} max={run.maxVitality} />
        <div style={{ flex: 1, minWidth: 80 }}>
          {isBoss && run.timer != null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: AF.m, fontSize: 13, fontWeight: 700, color: run.timer <= 10 ? 'var(--error)' : 'var(--accent)' }}>⏱ {run.timer}s</span>
              <div style={{ flex: 1, height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(run.timer / run.bossTimerMax) * 100}%`, background: run.timer <= 10 ? 'var(--error)' : 'var(--accent)', borderRadius: 3, transition: 'width 0.4s linear' }} />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 3 }}>
              {run.map.map((colNodes, i) => {
                const isBossCol = colNodes.length === 1 && colNodes[0].kind === 'boss'
                return (
                  <div key={i} style={{ flex: 1, height: 4, borderRadius: 2,
                    background: i < run.mapPos ? 'var(--success)' : i === run.mapPos ? (isBossCol ? 'var(--error)' : 'var(--accent)') : (isBossCol ? C.redBorder : C.border) }} />
                )
              })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Pollen earned this run — banked when you finish (forfeit if you wither).">
          <span style={{ fontSize: 12 }}>🌸</span>
          <span style={{ fontFamily: AF.m, fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{run.spores}</span>
        </div>
        <button onClick={() => { const v = !soundOn; sound.setEnabled(v); setSoundOn(v) }} title={soundOn ? 'Mute' : 'Unmute'} style={{ fontFamily: AF.b, fontSize: 13, color: C.dim, background: 'transparent', border: `1px solid ${C.border}`, padding: '5px 8px', borderRadius: 6, cursor: 'pointer' }}>{soundOn ? '🔊' : '🔇'}</button>
        <button onClick={onAbort} style={{ fontFamily: AF.b, fontSize: 11, color: C.dim, background: 'transparent', border: `1px solid ${C.border}`, padding: '5px 10px', borderRadius: 6, cursor: 'pointer' }}>Flee</button>
      </div>

      {/* combo banner */}
      <div style={{ padding: `8px ${hp} 0`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: AF.m, fontSize: 11, color: isBoss ? 'var(--error)' : C.dim, letterSpacing: '0.1em', fontWeight: isBoss ? 700 : 400 }}>
          {run.biome.glyph} {isBoss ? `CONJUGATION TRIAL${run.depth > 0 ? ` · DEPTH ${run.depth}` : ''}` : `${(run.current?.label ?? run.biome.name).toUpperCase()} · TRAIL ${run.mapPos + 1}/${run.map.length - 1}`}{run.groveMutated ? ' · 🌶️ OVERGROWN' : ''}
        </span>
        {run.combo >= 2 && <span style={{ fontFamily: AF.h, fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>🔥 {run.combo} bloom streak · ×{comboMultiplier(run.combo, run.comboCap + (run.traits.includes('bloomcaller') ? 1 : 0)).toFixed(1)}</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
          {run.traits.map(t => <span key={t} title={`${traitById(t).name} — ${traitById(t).blurb}`} style={{ fontSize: 14 }}>{traitById(t).glyph}</span>)}
        </span>
      </div>

      {/* exercise */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: `16px ${hp}`, maxWidth: 640, width: '100%', alignSelf: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{card.mode === 'quiz' ? '🎯' : '✏️'}</div>
          <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>{card.mode === 'quiz' ? 'Pick the correct conjugation' : 'Type the correct conjugation'}</span>
          {card.elite && <span style={{ fontFamily: AF.m, fontSize: 10, fontWeight: 700, color: '#B084E9', background: 'rgba(176,132,233,0.13)', border: '1px solid #B084E9', borderRadius: 14, padding: '2px 9px', letterSpacing: '0.06em' }}>◆ ELITE · ×2 · a verb you've struggled with</span>}
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: AF.h, fontSize: isMobile ? 46 : 68, fontWeight: 700, color: C.text, letterSpacing: '-2.5px', lineHeight: 1, marginBottom: 8 }}>{card.verb}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{card.tenseLabel}</span>
            <span style={{ color: C.border }}>·</span>
            <span style={{ fontFamily: AF.m, fontSize: 12, color: C.muted }}>{card.verbType}</span>
            <span style={{ color: C.border }}>·</span>
            <span style={{ fontFamily: AF.m, fontSize: isMobile ? 18 : 22, fontWeight: 500, color: C.text, letterSpacing: '-0.5px' }}>{card.pronoun}</span>
          </div>
          {card.translation && run.status !== 'feedback' && (
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.dim, marginTop: 6, fontStyle: 'italic' }}>{card.translation}</div>
          )}
        </div>

        {/* TYPE MODE */}
        {card.mode === 'type' && (
          <>
            <div className={isWrong ? 'c8-shake' : ''} style={{ borderRadius: 12, border: `2px solid ${isCorrect ? C.greenBorder : isWrong ? C.redBorder : C.border2}`, background: C.surf, marginBottom: 16, minHeight: 64, display: 'flex', alignItems: 'center', padding: '0 18px' }}>
              {run.status === 'card' ? (
                <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); checkTyped() } }}
                  placeholder="conjugation…" autoComplete="off" spellCheck={false}
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: AF.m, fontSize: isMobile ? 22 : 28, color: C.text, letterSpacing: '-0.5px', padding: '16px 0' }} />
              ) : (
                <div style={{ padding: '12px 0' }}>
                  {isWrong && <div style={{ fontFamily: AF.m, fontSize: isMobile ? 14 : 16, color: 'var(--error)', textDecoration: 'line-through', opacity: 0.65 }}>{input || '—'}</div>}
                  <div style={{ fontFamily: AF.m, fontSize: isMobile ? 22 : 28, fontWeight: 500, color: isCorrect ? 'var(--success)' : C.text }}>{card.answer}</div>
                  <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginTop: 2 }}>{card.translation}</div>
                </div>
              )}
            </div>
            {run.status === 'card' ? (
              <button onClick={checkTyped} style={{ alignSelf: 'flex-start', fontFamily: AF.b, fontSize: 14, fontWeight: 700, background: input.trim() ? 'var(--accent)' : C.surf2, color: input.trim() ? C.bg : C.dim, border: 'none', padding: '13px 26px', borderRadius: 9, cursor: input.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 9 }}>Check <Kbd>↵</Kbd></button>
            ) : <FeedbackBar fb={fb} onNext={next} />}
          </>
        )}

        {/* QUIZ MODE */}
        {card.mode === 'quiz' && card.options && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {card.options.map((opt, i) => {
                const showRight = chosen !== null && opt === card.answer
                const showWrong = chosen === opt && opt !== card.answer
                let bgc = C.surf, bd = C.border2, col = C.text
                if (showRight) { bgc = C.greenBg; bd = C.greenBorder; col = 'var(--success)' }
                if (showWrong) { bgc = C.redBg; bd = C.redBorder; col = 'var(--error)' }
                return (
                  <button key={opt} onClick={() => chooseQuiz(opt)} disabled={chosen !== null} style={{
                    fontFamily: AF.m, fontSize: isMobile ? 17 : 22, fontWeight: 500, color: col, background: bgc,
                    border: `2px solid ${bd}`, padding: `${isMobile ? 14 : 18}px 14px`, borderRadius: 11,
                    cursor: chosen !== null ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                    wordBreak: 'break-word', overflowWrap: 'break-word',
                  }}>
                    <span style={{ fontFamily: AF.b, fontSize: 11, fontWeight: 700, color: showRight ? 'var(--success)' : showWrong ? 'var(--error)' : C.dim, display: 'flex', alignItems: 'center', gap: 5 }}>
                      {showRight ? '✓' : showWrong ? '✗' : <Kbd>{String(i + 1)}</Kbd>}
                    </span>
                    {opt}
                  </button>
                )
              })}
            </div>
            {run.status === 'feedback' && <FeedbackBar fb={fb} onNext={next} />}
          </>
        )}
      </div>
    </div>
  )
}

function FeedbackBar({ fb, onNext }: { fb: LastResult; onNext: () => void }) {
  if (!fb) return null
  return (
    <div className="c8-pop" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <button onClick={onNext} style={{
        fontFamily: AF.b, fontSize: 14, fontWeight: 700,
        background: fb.correct ? 'var(--success)' : C.surf, color: fb.correct ? C.bg : C.text,
        border: `1px solid ${fb.correct ? C.greenBorder : C.border}`, padding: '13px 26px', borderRadius: 9, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 9,
      }}>Continue <Kbd>↵</Kbd></button>
      {fb.correct ? (
        <span style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 700, color: fb.crit ? '#B084E9' : 'var(--accent)' }}>{fb.crit ? '⚡ CRIT ' : ''}+{fb.gained} 🌸</span>
      ) : (
        <span style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 700, color: 'var(--error)' }}>{fb.vitLost === 0 ? '🪵 shielded' : `−${fb.vitLost} 🌿`}{fb.gained > 0 ? <span style={{ color: C.muted, fontSize: 13, marginLeft: 8 }}>♻️ +{fb.gained} composted</span> : null}</span>
      )}
      {fb.grew && <span className="c8-pop" style={{ fontFamily: AF.b, fontSize: 13, fontWeight: 700, color: 'var(--success)' }}>🌿 {fb.grew} grew into a specimen! +{MASTERY_BONUS} 🌸</span>}
    </div>
  )
}

// ══ RUN SUMMARY ═══════════════════════════════════════════════════════════════
function CEFRMiniCard({ langCode }: { langCode: string }) {
  const [grade, setGrade] = useState<ReturnType<typeof computeCEFR> | null>(null)
  useEffect(() => { setGrade(computeCEFR(langCode)) }, [langCode])
  if (!grade) return null
  return (
    <div style={{ padding: '12px 16px', borderRadius: 11, background: C.surf, border: `1px solid ${C.border}`, marginBottom: 24, textAlign: 'left' }}>
      <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.12em', marginBottom: 10 }}>
        📊 YOUR LANGUAGE LEVEL
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ fontFamily: AF.h, fontSize: 30, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-0.5px', lineHeight: 1 }}>
          {grade.label}
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ fontFamily: AF.m, fontSize: 10, color: C.muted }}>
              {grade.label === 'C2' ? 'Mastery reached' : `${grade.progressToNext}% to next`}
            </span>
            <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim }}>{grade.overall}/100</span>
          </div>
          <div style={{ height: 5, background: C.surf2, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${grade.progressToNext}%`, height: '100%', background: 'var(--accent)', borderRadius: 3 }} />
          </div>
          {grade.cappedBy && grade.weakest && (
            <div style={{ fontFamily: AF.b, fontSize: 11, color: 'var(--warn)', marginTop: 6, lineHeight: 1.4 }}>
              🔒 {grade.weakest.name} is holding you back — practice it to unlock the next level
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RunSummary({ run, state, won, onDone }: { run: RunState; state: GardenState; won: boolean; onDone: () => void }) {
  const isMobile = useIsMobile()
  useEffect(() => { won ? sound.victory() : sound.wither() }, [won])
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDone() } }
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h)
  }, [onDone])
  const banked = bankedPollen(run, state)
  const heartwood = won ? HEARTWOOD_PER_BOSS + run.depth : 0
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: won ? C.greenBg : C.bg, padding: '24px' }}>
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>{won ? '🏆' : '🥀'}</div>
        <h2 style={{ fontFamily: AF.h, fontSize: 30, fontWeight: 700, color: C.text, letterSpacing: '-0.9px', margin: '0 0 8px' }}>
          {won ? 'Trial conquered!' : 'Your expedition withered'}
        </h2>
        <p style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, margin: '0 0 28px', lineHeight: 1.5 }}>
          {won
            ? `You broke the Hush at the heart of ${run.biome.name}${run.depth > 0 ? ` at Depth ${run.depth}` : ''} and carried the whole harvest home.`
            : `You fell at ${run.current?.kind === 'boss' ? 'the Conjugation Trial' : `the ${run.current?.label ?? 'wilds'}`}. The unbanked harvest is lost — but the words you woke remain.`}
        </p>
        <StatLine style={{ justifyContent: 'center', marginBottom: 14 }} items={[
          { label: won ? '🌸 pollen' : '🌸 forfeit', value: `+${banked.toLocaleString()}`, color: 'var(--accent)' },
          ...(won && heartwood > 0 ? [{ label: '🪵 heartwood', value: `+${heartwood}`, color: 'var(--accent)' }] : []),
          { label: '🌿 grown', value: run.newlyMastered.length, color: 'var(--success)' },
          ...(run.reviewHits.length > 0 ? [{ label: '🩹 shored up', value: run.reviewHits.length, color: '#B084E9' }] : []),
          { label: '🔥 best', value: run.bestCombo },
        ]} />
        {Object.keys(run.discoveries).length > 0 && (
          <div style={{ padding: '12px 16px', borderRadius: 11, background: C.surf, border: `1px solid ${C.border}`, marginBottom: 14 }}>
            <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.12em', marginBottom: 8 }}>📒 {Object.keys(run.discoveries).length} NEW SPECIES CATALOGUED</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              {Object.entries(run.discoveries).map(([v, r]) => (
                <span key={v} style={{ fontFamily: AF.m, fontSize: 12, color: RARITY_META[r].color, background: RARITY_META[r].bg, border: `1px solid ${RARITY_META[r].color}`, borderRadius: 14, padding: '3px 10px' }}>{RARITY_META[r].sigil} {v}</span>
              ))}
            </div>
          </div>
        )}
        {run.newlyMastered.length > 0 && (
          <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 14 }}>
            Grown into specimens: {run.newlyMastered.map(v => <span key={v} style={{ color: 'var(--success)', fontFamily: AF.m }}>{v}</span>).reduce((acc, el, i) => i === 0 ? [el] : [...acc, <span key={'s' + i}>, </span>, el] as any, [] as any)}
          </div>
        )}
        <CEFRMiniCard langCode={run.langCode} />
        <button onClick={onDone} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: AF.b, fontSize: 15, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '14px 34px', borderRadius: 10, cursor: 'pointer' }}>
          Return to garden → <Kbd>↵</Kbd>
        </button>
      </div>
    </div>
  )
}

// ══ SHARED: restoration ring ══════════════════════════════════════════════════
function RestorationRing({ pct, size = 96, label = true }: { pct: number; size?: number; label?: boolean }) {
  const R = size / 2 - 7
  const CIRC = 2 * Math.PI * R
  const col = pct >= 100 ? 'var(--success)' : pct >= 50 ? 'var(--accent)' : 'var(--warn)'
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="var(--surf3)" strokeWidth="7" />
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke={col} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - pct / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      {label && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: AF.h, fontSize: size * 0.26, fontWeight: 700, color: col, lineHeight: 1 }}>{pct}%</span>
          <span style={{ fontFamily: AF.m, fontSize: 8.5, color: C.dim, letterSpacing: '0.1em', marginTop: 2 }}>RESTORED</span>
        </div>
      )}
    </div>
  )
}

// ══ TAB BAR ═══════════════════════════════════════════════════════════════════
type Tab = 'garden' | 'chronicle' | 'verbarium' | 'profile' | 'leaderboard' | 'friends'
const TABS: { id: Tab; label: string; glyph: string }[] = [
  { id: 'garden', label: 'Garden', glyph: '🌿' },
  { id: 'chronicle', label: 'Chronicle', glyph: '📖' },
  { id: 'verbarium', label: 'Verbarium', glyph: '📒' },
  { id: 'profile', label: 'Profile', glyph: '🪪' },
  { id: 'leaderboard', label: 'Leaderboard', glyph: '🏆' },
  { id: 'friends', label: 'Wardens', glyph: '🤝' },
]
function TabBar({ active, onSelect, friendCount }: { active: Tab; onSelect: (t: Tab) => void; friendCount: number }) {
  return (
    <div style={{ display: 'flex', gap: 4, padding: '8px 12px', borderBottom: `1px solid ${C.border}`, background: C.surf, overflowX: 'auto', flexShrink: 0 }}>
      {TABS.map((t, i) => {
        const on = active === t.id
        return (
          <button key={t.id} onClick={() => onSelect(t.id)} title={`${t.label} — press ${i + 1}`} style={{
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            fontFamily: AF.b, fontSize: 13, fontWeight: on ? 700 : 400,
            padding: '7px 12px', borderRadius: 20, cursor: 'pointer',
            border: `1px solid ${on ? 'var(--accent)' : C.border}`,
            background: on ? 'var(--accent)' : 'transparent', color: on ? C.bg : C.muted,
          }}>
            <span style={{ fontFamily: AF.m, fontSize: 9, opacity: 0.6 }}>{i + 1}</span>
            <span style={{ fontSize: 13 }}>{t.glyph}</span>{t.label}
            {t.id === 'friends' && friendCount > 0 && <span style={{ fontFamily: AF.m, fontSize: 10, opacity: 0.8 }}>{friendCount}</span>}
          </button>
        )
      })}
    </div>
  )
}

// ══ CHRONICLE (story codex) ═══════════════════════════════════════════════════
function ChronicleView({ state }: { state: GardenState }) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '48px'
  const act = currentAct(state)
  const obj = actObjective(state)
  const r = restorationPct(state)
  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: `24px ${hp} 72px` }}>
        {/* World state */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '18px 20px', borderRadius: 14, border: `1px solid ${C.border}`, background: C.surf, marginBottom: 14, flexWrap: 'wrap' }}>
          <RestorationRing pct={r} size={isMobile ? 84 : 100} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 4 }}>THE VERDANT</div>
            <div style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px', marginBottom: 6 }}>
              {r >= 100 ? 'The Hush is broken' : r >= 50 ? 'The Hush is in retreat' : r > 0 ? 'The Hush still holds' : 'A world gone grey'}
            </div>
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
              {COMPANION.glyph} <span style={{ fontStyle: 'italic' }}>“{r >= 100 ? 'You spoke it all back, Warden. All of it.' : `${hushPct(state)}% of the Verdant is still silent. Keep speaking.`}”</span> — {COMPANION.name}, {COMPANION.role}
            </div>
          </div>
        </div>

        {/* Current objective */}
        <div style={{ padding: '16px 18px', borderRadius: 12, border: `1px solid ${C.accentBorder}`, background: C.accentBg, marginBottom: 26 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
            <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.12em' }}>ACT {act.roman} · NOW</span>
            <span style={{ fontFamily: AF.m, fontSize: 11, color: C.muted }}>{obj.pct}%</span>
          </div>
          <div style={{ fontFamily: AF.b, fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 10 }}>{obj.label}</div>
          <div style={{ height: 6, background: C.surf2, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${obj.pct}%`, background: 'var(--accent)', borderRadius: 3, transition: 'width 0.5s' }} />
          </div>
        </div>

        {/* Acts */}
        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 14 }}>THE CHRONICLE OF THE HUSH</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {ACTS.map((a, i) => {
            const points = storyPoints(state)
            const unlocked = points >= a.unlockAt
            const cleared = act.num > a.num || (act.num === a.num && r >= 100 && a.num === ACTS.length)
            const isCurrent = act.num === a.num
            return (
              <div key={a.id} style={{ display: 'flex', gap: 14 }}>
                {/* timeline rail */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    background: cleared ? 'var(--success)' : isCurrent ? 'var(--accent)' : C.surf2,
                    border: `1px solid ${cleared ? C.greenBorder : isCurrent ? 'var(--accent)' : C.border}`,
                    color: cleared || isCurrent ? C.bg : C.dim, fontFamily: AF.h, fontSize: 11, fontWeight: 700 }}>
                    {cleared ? '✓' : a.roman}
                  </div>
                  {i < ACTS.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 28, background: unlocked ? 'var(--success)' : C.border, opacity: unlocked ? 0.4 : 1 }} />}
                </div>
                {/* content */}
                <div style={{ flex: 1, paddingBottom: 22, opacity: unlocked ? 1 : 0.5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: isCurrent ? 'var(--accent)' : C.text, letterSpacing: '-0.3px' }}>{a.title}</span>
                    {isCurrent && <span style={{ fontFamily: AF.m, fontSize: 9, color: 'var(--accent)', border: `1px solid ${C.accentBorder}`, borderRadius: 4, padding: '1px 6px' }}>CURRENT</span>}
                    {cleared && <span style={{ fontFamily: AF.m, fontSize: 9, color: 'var(--success)' }}>cleared</span>}
                  </div>
                  <div style={{ fontFamily: AF.b, fontSize: 12.5, color: C.muted, fontStyle: 'italic', marginTop: 3 }}>{a.tagline}</div>
                  {unlocked ? (
                    <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginTop: 8, lineHeight: 1.6 }}>
                      {a.intro[0]}
                      <div style={{ marginTop: 8, padding: '8px 12px', borderLeft: `2px solid ${C.border2}`, background: C.surf, borderRadius: '0 6px 6px 0' }}>
                        <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, letterSpacing: '0.08em' }}>FRAGMENT · </span>
                        <span style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, fontStyle: 'italic' }}>{a.lore}</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontFamily: AF.m, fontSize: 12, color: C.dim, marginTop: 8 }}>🔒 Sealed by the Hush — restore more of the Verdant to uncover this chapter.</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ══ ACHIEVEMENTS GRID ═════════════════════════════════════════════════════════
const TIER_COLOR: Record<Achievement['tier'], string> = { bronze: 'var(--accent-2, #E05520)', silver: 'var(--muted)', gold: 'var(--accent)' }
function AchievementsGrid({ state, compact }: { state: GardenState; compact?: boolean }) {
  const isMobile = useIsMobile()
  const evald = evaluateAchievements(state)
  const got = evald.filter(e => e.unlocked).length
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em' }}>ACHIEVEMENTS</div>
        <div style={{ fontFamily: AF.m, fontSize: 11, color: C.muted }}>{got}/{ACHIEVEMENTS.length}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : compact ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)', gap: 8 }}>
        {evald.map(({ def, unlocked }) => (
          <div key={def.id} title={def.desc} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
            background: unlocked ? C.surf : 'transparent',
            border: `1px solid ${unlocked ? TIER_COLOR[def.tier] : C.border}`,
            opacity: unlocked ? 1 : 0.5,
          }}>
            <span style={{ fontSize: 20, flexShrink: 0, filter: unlocked ? 'none' : 'grayscale(1)' }}>{unlocked ? def.glyph : '🔒'}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: AF.h, fontSize: 12.5, fontWeight: 700, color: unlocked ? C.text : C.dim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{def.name}</div>
              <div style={{ fontFamily: AF.b, fontSize: 10.5, color: C.dim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{def.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ══ VERBARIUM (the species Pokédex) ═══════════════════════════════════════════
function validVerbs(verbData: Record<string, any>, langCode: string): string[] {
  return Object.keys(verbData).filter(v => {
    if (langCode === 'fi') return true
    if (v.endsWith('se')) return false
    return ['ar', 'er', 'ir'].includes(v.slice(-2))
  })
}

function SpeciesPortrait({ verb, size = 56, dim, language }: { verb: string; size?: number; dim?: boolean; language: Language }) {
  const seed = hashString(verb)
  return (
    <svg viewBox="0 0 60 52" width={size} height={size * 52 / 60} style={{ filter: dim ? 'brightness(0.18) grayscale(1)' : 'none', flexShrink: 0 }}>
      <Plant x={30} groundY={46} seed={seed} scale={1.2} opacity={1} language={language} />
    </svg>
  )
}

function VerbDetailPanel({ verb, langCode, verbData, state, language, onClose }: { verb: string; langCode: string; verbData: Record<string, any>; state: GardenState; language: Language; onClose: () => void }) {
  const d = state.discovered[verb]
  const rm = d ? RARITY_META[d.rarity] : null
  const caught = state.specimens[verb]?.mastered
  const pronouns = pronounsFor(langCode)
  const tenses: ConjTense[] = langCode === 'fi' ? ['present', 'conditional', 'present_perfect'] : ['present', 'preterite', 'imperfect']
  const ePresent: string | undefined = verbData[verb]?.e_present
  const translation = ePresent ? `to ${ePresent}` : ''
  const seed = hashString(verb)

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 600, maxHeight: '85vh', overflowY: 'auto', background: 'var(--bg)', borderRadius: '18px 18px 0 0', padding: '24px 20px 40px' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <SpeciesPortrait verb={verb} size={56} language={language} />
            <div>
              <div style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color: C.text, letterSpacing: '-0.8px', lineHeight: 1 }}>{verb}</div>
              {translation && <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginTop: 4, fontStyle: 'italic' }}>{translation}</div>}
              {rm && <div style={{ fontFamily: AF.m, fontSize: 10, color: rm.color, letterSpacing: '0.06em', marginTop: 4 }}>{rm.sigil} {rm.label.toUpperCase()} · {caught ? '🌿 Caught' : '◌ Seen'}</div>}
            </div>
          </div>
          <button onClick={onClose} style={{ fontFamily: AF.b, fontSize: 18, color: C.dim, background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>✕</button>
        </div>

        {/* mastery bar */}
        {state.specimens[verb] && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: AF.b, fontSize: 12, color: C.muted, marginBottom: 5 }}>
              <span>Mastery</span>
              <span>{state.specimens[verb].correct ?? 0} / {MASTERY_THRESHOLD} correct</span>
            </div>
            <div style={{ height: 6, background: C.surf2, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(100, ((state.specimens[verb].correct ?? 0) / MASTERY_THRESHOLD) * 100)}%`, background: 'var(--success)', borderRadius: 3, transition: 'width 0.4s' }} />
            </div>
          </div>
        )}

        {/* conjugation tables */}
        {tenses.map(tense => {
          const rows = pronouns.map(p => ({
            pronoun: p,
            form: deriveConjugation(verb, p, tense, langCode, verbData),
          })).filter(r => r.form && r.form !== verb)
          if (!rows.length) return null
          return (
            <div key={tense} style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>{tense.replace(/_/g, ' ')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {rows.map(({ pronoun, form }) => (
                  <div key={pronoun} style={{ display: 'flex', gap: 8, padding: '7px 10px', borderRadius: 8, background: C.surf, border: `1px solid ${C.border}` }}>
                    <span style={{ fontFamily: AF.m, fontSize: 12, color: C.muted, minWidth: 60 }}>{pronoun}</span>
                    <span style={{ fontFamily: AF.m, fontSize: 13, fontWeight: 600, color: C.text }}>{form}</span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function VerbariumView({ state, verbData, langCode }: { state: GardenState; verbData: Record<string, any>; langCode: string }) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '16px' : '48px'
  const language = langCodeToName(langCode)
  const [filter, setFilter] = useState<'all' | Rarity | 'caught'>('all')
  const [selected, setSelected] = useState<string | null>(null)

  const total = Math.max(validVerbs(verbData, langCode).length, Object.keys(state.discovered).length)
  const discovered = Object.values(state.discovered)
  const caughtCount = discovered.filter(d => state.specimens[d.verb]?.mastered).length
  const byRarity = (['legendary', 'epic', 'rare', 'uncommon', 'common'] as Rarity[]).map(r => ({ r, n: discovered.filter(d => d.rarity === r).length }))

  let shown = [...discovered]
  if (filter === 'caught') shown = shown.filter(d => state.specimens[d.verb]?.mastered)
  else if (filter !== 'all') shown = shown.filter(d => d.rarity === filter)
  shown.sort((a, b) => RARITY_META[b.rarity].order - RARITY_META[a.rarity].order || b.firstSeen - a.firstSeen)

  const hidden = Math.max(0, total - discovered.length)
  const teasers = Math.min(hidden, filter === 'all' ? (isMobile ? 4 : 6) : 0)

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: `22px ${hp} 72px` }}>
        {/* completion */}
        <div style={{ padding: '16px 18px', borderRadius: 13, border: `1px solid ${C.border}`, background: C.surf, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
            <div style={{ fontFamily: AF.h, fontSize: 19, fontWeight: 700, color: C.text, letterSpacing: '-0.4px' }}>Verbarium</div>
            <div style={{ fontFamily: AF.m, fontSize: 12, color: C.muted }}><span style={{ color: 'var(--accent)', fontWeight: 700 }}>{discovered.length}</span> / {total} discovered · {caughtCount} caught</div>
          </div>
          <div style={{ height: 7, background: C.surf2, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${total > 0 ? (discovered.length / total) * 100 : 0}%`, background: 'linear-gradient(90deg, var(--success), var(--accent))', borderRadius: 4, transition: 'width 0.5s' }} />
          </div>
          <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 10 }}>Every verb you meet in the wild is catalogued here. Master one ({MASTERY_THRESHOLD} correct) to <em>catch</em> it — it grows into a living specimen.</div>
        </div>

        {/* rarity filter */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {([['all', `All ${discovered.length}`], ['caught', `🌿 Caught ${caughtCount}`], ...byRarity.filter(x => x.n > 0).map(x => [x.r, `${RARITY_META[x.r].sigil} ${RARITY_META[x.r].label} ${x.n}`] as [string, string])] as [string, string][]).map(([id, label]) => {
            const on = filter === id
            const col = id === 'all' || id === 'caught' ? 'var(--accent)' : RARITY_META[id as Rarity].color
            return (
              <button key={id} onClick={() => setFilter(id as any)} style={{ fontFamily: AF.b, fontSize: 12, fontWeight: on ? 700 : 400, padding: '6px 13px', borderRadius: 18, cursor: 'pointer', border: `1px solid ${on ? col : C.border}`, background: on ? col : 'transparent', color: on ? C.bg : C.muted }}>{label}</button>
            )
          })}
        </div>

        {discovered.length === 0 ? (
          <div style={{ padding: '34px 24px', borderRadius: 13, border: `1px dashed ${C.border2}`, background: C.surf, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📒</div>
            <div style={{ fontFamily: AF.h, fontSize: 17, color: C.muted, marginBottom: 6 }}>The Verbarium is empty</div>
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.dim }}>Begin an expedition — every new verb you meet appears here as a species to discover and catch.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 10 }}>
            {shown.map(d => {
              const rm = RARITY_META[d.rarity]
              const caught = state.specimens[d.verb]?.mastered
              return (
                <button key={d.verb} onClick={() => setSelected(d.verb)} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: rm.bg, border: `1px solid ${rm.color}`, overflow: 'hidden', cursor: 'pointer', textAlign: 'left' }}>
                  <SpeciesPortrait verb={d.verb} size={48} language={language} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: C.text, letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.verb}</div>
                    <div style={{ fontFamily: AF.m, fontSize: 10, color: rm.color, letterSpacing: '0.06em' }}>{rm.sigil} {rm.label.toUpperCase()}</div>
                    <div style={{ fontFamily: AF.b, fontSize: 11, color: caught ? 'var(--success)' : C.dim, marginTop: 3 }}>{caught ? '🌿 Caught' : '◌ Seen'}</div>
                  </div>
                </button>
              )
            })}
            {Array.from({ length: teasers }).map((_, i) => (
              <div key={'t' + i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: C.surf, border: `1px dashed ${C.border2}`, opacity: 0.6 }}>
                <SpeciesPortrait verb={'hidden' + i} size={48} dim language={language} />
                <div>
                  <div style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: C.dim }}>? ? ?</div>
                  <div style={{ fontFamily: AF.m, fontSize: 10, color: C.dim }}>UNDISCOVERED</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {hidden > 0 && filter === 'all' && (
          <div style={{ fontFamily: AF.b, fontSize: 12, color: C.dim, textAlign: 'center', marginTop: 18 }}>🌫️ {hidden.toLocaleString()} more species still silenced in the Hush — go find them.</div>
        )}
      </div>

      {selected && (
        <VerbDetailPanel
          verb={selected}
          langCode={langCode}
          verbData={verbData}
          state={state}
          language={language}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

// ══ AUTH: sign-in gate + header chip ══════════════════════════════════════════
function isLocalhost() {
  return typeof window !== 'undefined' && /^(localhost|127\.|0\.0\.0\.0|\[?::1)/.test(window.location.hostname)
}

function SignInGate({ feature, sub }: { feature: string; sub: string }) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '48px'
  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 460, margin: '0 auto', padding: `48px ${hp} 72px`, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 14 }}>🔒</div>
        <h2 style={{ fontFamily: AF.h, fontSize: 24, fontWeight: 700, color: C.text, letterSpacing: '-0.6px', margin: '0 0 8px' }}>Sign in to unlock {feature}</h2>
        <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, lineHeight: 1.55, margin: '0 0 24px' }}>{sub} Your solo garden and the Chronicle stay free — only the social features need an account.</p>
        <a href={authUrls.login} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: AF.b, fontSize: 15, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '13px 28px', borderRadius: 10, cursor: 'pointer', textDecoration: 'none' }}>
          Sign in with Auth0
        </a>
        {isLocalhost() && (
          <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px dashed ${C.border2}` }}>
            <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 10 }}>DEV SIGN-IN (LOCAL ONLY)</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {['Vera Linden', 'Bram', 'Sol'].map(n => (
                <a key={n} href={authUrls.devLogin(n)} style={{ fontFamily: AF.b, fontSize: 12, color: C.text, background: C.surf, border: `1px solid ${C.border}`, padding: '8px 14px', borderRadius: 8, textDecoration: 'none' }}>as {n}</a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function AuthChip() {
  const me = useMe()
  if (me.isLoading) return null
  if (!me.authenticated) {
    return <a href={authUrls.login} style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: C.bg, background: 'var(--accent)', padding: '7px 14px', borderRadius: 20, textDecoration: 'none', whiteSpace: 'nowrap' }}>Sign in</a>
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 16 }}>{me.profile?.avatar}</span>
      <span style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{me.profile?.handle}</span>
      <a href={isLocalhost() ? authUrls.devLogout : authUrls.logout} style={{ fontFamily: AF.b, fontSize: 11, color: C.dim, border: `1px solid ${C.border}`, padding: '5px 10px', borderRadius: 7, textDecoration: 'none' }}>Sign out</a>
    </div>
  )
}

// ══ PROFILE (public Warden card) ══════════════════════════════════════════════
type CardData = { handle: string; avatar: string; bio: string; restoration: number; specimens: number; biomesCleared: number; bestStreak: number; pollen: number; wins: number; totalCorrect: number; lastActive: string }

function ProfileView({ langCode, state, localProfile, onSaveLocal, viewUserId }: {
  langCode: string; state: GardenState; localProfile: Profile; onSaveLocal: (p: Profile) => void; viewUserId: string | null
}) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '48px'
  const me = useMe()
  const updateProfile = useUpdateProfile()
  const toggleFriend = useToggleFriend(langCode)
  const other = useWardenProfile(viewUserId, langCode)

  const isYou = !viewUserId
  const [editing, setEditing] = useState(false)
  const [copied, setCopied] = useState(false)

  // Build the display card from server (other / authed self) or local (offline self).
  let card: CardData
  let title: string
  let score: number
  if (viewUserId) {
    const p = other.data
    card = {
      handle: p?.profile?.handle ?? 'Warden', avatar: p?.profile?.avatar ?? '🌿', bio: p?.profile?.bio ?? '',
      restoration: p?.summary?.restoration ?? 0, specimens: p?.summary?.specimens ?? 0, biomesCleared: p?.summary?.biomesCleared ?? 0,
      bestStreak: p?.summary?.bestStreak ?? 0, pollen: p?.summary?.pollen ?? 0, wins: p?.summary?.wins ?? 0, totalCorrect: p?.summary?.totalCorrect ?? 0,
      lastActive: '—',
    }
    title = p?.title ?? wardenTitle(card.restoration); score = p?.score ?? 0
  } else {
    const local = youAsPlayer(state, localProfile)
    const handle = me.authenticated ? (me.profile?.handle ?? local.handle) : local.handle
    const avatar = me.authenticated ? (me.profile?.avatar ?? local.avatar) : local.avatar
    const bio = me.authenticated ? (me.profile?.bio ?? '') : localProfile.bio
    card = { handle, avatar, bio, restoration: local.restoration, specimens: local.specimens, biomesCleared: local.biomesCleared, bestStreak: local.bestStreak, pollen: local.pollen, wins: local.wins, totalCorrect: local.totalCorrect, lastActive: 'now' }
    title = wardenTitle(card.restoration); score = wardenScore(card)
  }

  const [handle, setHandle] = useState(card.handle)
  const [avatar, setAvatar] = useState(card.avatar)
  const [bio, setBio] = useState(card.bio)
  useEffect(() => { if (editing) { setHandle(card.handle); setAvatar(card.avatar); setBio(card.bio) } }, [editing])

  const saveEdits = () => {
    if (me.authenticated) updateProfile.mutate({ handle: handle.trim() || card.handle, avatar, bio: bio.trim() })
    else onSaveLocal({ ...localProfile, handle: handle.trim() || localProfile.handle, avatar, bio: bio.trim() })
    setEditing(false)
  }

  const share = () => {
    const url = typeof window !== 'undefined' ? window.location.href.split('#')[0] : ''
    try { navigator.clipboard?.writeText(`${card.avatar} ${card.handle} — ${title} · ${card.restoration}% of the Verdant restored · Warden Score ${score.toLocaleString()} · ${url}`) } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 1800)
  }

  const isFriend = !!viewUserId && (me.profile?.friends?.includes(viewUserId) ?? false)

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: `24px ${hp} 72px` }}>
        {isYou && !me.authenticated && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 16px', borderRadius: 11, border: `1px solid ${C.accentBorder}`, background: C.accentBg, marginBottom: 16 }}>
            <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>🔒 This card is private. Sign in to publish it and join the leaderboard.</span>
            <a href={authUrls.login} style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: C.bg, background: 'var(--accent)', padding: '7px 14px', borderRadius: 8, textDecoration: 'none', whiteSpace: 'nowrap' }}>Sign in</a>
          </div>
        )}

        {/* Card */}
        <div style={{ borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.surf, marginBottom: 18 }}>
          <div style={{ position: 'relative', height: 78, overflow: 'hidden', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, var(--accent-bg), transparent)' }} />
            <div style={{ position: 'absolute', right: -20, top: -10, fontSize: 120, opacity: 0.12 }}>{card.avatar}</div>
          </div>
          <div style={{ padding: '0 20px 20px', marginTop: -34 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ width: 68, height: 68, borderRadius: 16, background: C.surf2, border: `2px solid ${C.border2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, flexShrink: 0 }}>{card.avatar}</div>
              <div style={{ flex: 1, minWidth: 160, paddingBottom: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: '-0.5px' }}>{viewUserId && other.isLoading ? '…' : card.handle}</span>
                  {isYou && <span style={{ fontFamily: AF.m, fontSize: 9, color: 'var(--accent)', border: `1px solid ${C.accentBorder}`, borderRadius: 4, padding: '2px 7px' }}>YOU</span>}
                </div>
                <div style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{title}</div>
                {card.bio && <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 3 }}>{card.bio}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
                {isYou ? (
                  <>
                    <button onClick={share} style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, background: copied ? C.greenBg : C.accentBg, color: copied ? 'var(--success)' : 'var(--accent)', border: `1px solid ${copied ? C.greenBorder : C.accentBorder}`, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' }}>{copied ? '✓ Copied' : '🔗 Share'}</button>
                    <button onClick={() => setEditing(e => !e)} style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}>{editing ? 'Close' : 'Edit'}</button>
                  </>
                ) : me.authenticated ? (
                  <button onClick={() => viewUserId && toggleFriend.mutate({ friendId: viewUserId, add: !isFriend })} style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, background: isFriend ? C.surf2 : 'var(--accent)', color: isFriend ? C.muted : C.bg, border: isFriend ? `1px solid ${C.border}` : 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' }}>{isFriend ? '✓ Warden-friend' : '+ Add Warden'}</button>
                ) : null}
              </div>
            </div>

            {/* Editor */}
            {isYou && editing && (
              <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: C.surf2, border: `1px solid ${C.border}` }}>
                <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.12em', marginBottom: 8 }}>CHOOSE A SIGIL</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {AVATARS.map(a => (
                    <button key={a} onClick={() => setAvatar(a)} style={{ fontSize: 18, width: 36, height: 36, borderRadius: 8, cursor: 'pointer', background: avatar === a ? C.accentBg : C.surf, border: `1px solid ${avatar === a ? 'var(--accent)' : C.border}` }}>{a}</button>
                  ))}
                </div>
                <input value={handle} onChange={e => setHandle(e.target.value.slice(0, 20))} placeholder="Warden name" style={{ width: '100%', boxSizing: 'border-box', fontFamily: AF.b, fontSize: 14, color: C.text, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8, outline: 'none' }} />
                <input value={bio} onChange={e => setBio(e.target.value.slice(0, 60))} placeholder="A short motto (optional)" style={{ width: '100%', boxSizing: 'border-box', fontFamily: AF.b, fontSize: 13, color: C.text, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, outline: 'none' }} />
                <button onClick={saveEdits} style={{ fontFamily: AF.b, fontSize: 13, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '10px 22px', borderRadius: 8, cursor: 'pointer' }}>Save</button>
              </div>
            )}
          </div>
        </div>

        {/* Headline stats + ring */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <RestorationRing pct={card.restoration} size={isMobile ? 78 : 88} />
          <StatLine style={{ flex: 1, minWidth: 220 }} items={[
            { label: 'score', value: score.toLocaleString(), color: 'var(--accent)' },
            { label: 'specimens', value: card.specimens },
            { label: 'biomes', value: card.biomesCleared },
            { label: 'best 🔥', value: card.bestStreak },
          ]} />
        </div>

        {isYou ? (
          <div style={{ marginTop: 8 }}><AchievementsGrid state={state} /></div>
        ) : (
          <StatLine items={[
            { label: 'lifetime 🌸', value: card.pollen.toLocaleString() },
            { label: 'trials won', value: card.wins },
            { label: 'words spoken', value: card.totalCorrect.toLocaleString() },
            { label: 'last seen', value: card.lastActive },
          ]} />
        )}
      </div>
    </div>
  )
}

// ══ LEADERBOARD (server-backed) ═══════════════════════════════════════════════
function LeaderboardView({ langCode, onViewWarden }: { langCode: string; onViewWarden: (userId: string) => void }) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '14px' : '48px'
  const me = useMe()
  const [scope, setScope] = useState<'global' | 'friends'>('global')
  const lb = useLeaderboard(scope, langCode, me.authenticated)

  if (me.isLoading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}><span style={{ fontFamily: AF.b, color: C.muted }}>…</span></div>
  if (!me.authenticated) return <SignInGate feature="the leaderboard" sub="Publish your garden and rank against Wardens worldwide." />

  const rows = (lb.data?.entries ?? []).map((e, i) => ({ ...e, rank: i + 1 }))
  const medal = (rank: number) => rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: `22px ${hp} 72px` }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          {(['global', 'friends'] as const).map(s => (
            <button key={s} onClick={() => setScope(s)} style={{ fontFamily: AF.b, fontSize: 13, fontWeight: scope === s ? 700 : 400, padding: '8px 18px', borderRadius: 20, cursor: 'pointer', border: `1px solid ${scope === s ? 'var(--accent)' : C.border}`, background: scope === s ? 'var(--accent)' : 'transparent', color: scope === s ? C.bg : C.muted }}>
              {s === 'global' ? '🌍 Global' : '🤝 Wardens'}
            </button>
          ))}
        </div>
        {lb.isLoading && <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, padding: '8px 2px' }}>Reading the rolls…</div>}
        {scope === 'friends' && !lb.isLoading && rows.length <= 1 && (
          <div style={{ padding: '16px 18px', borderRadius: 10, border: `1px dashed ${C.border2}`, background: C.surf, fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 14 }}>
            You haven't befriended any Wardens yet. Open the <strong style={{ color: C.text }}>Wardens</strong> tab to find some.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(p => {
            const m = medal(p.rank)
            return (
              <button key={p.userId} onClick={() => !p.isYou && onViewWarden(p.userId)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 11, textAlign: 'left',
                cursor: p.isYou ? 'default' : 'pointer',
                border: `1px solid ${p.isYou ? 'var(--accent)' : C.border}`,
                background: p.isYou ? C.accentBg : C.surf,
              }}>
                <div style={{ width: 30, textAlign: 'center', flexShrink: 0, fontFamily: AF.h, fontSize: m ? 18 : 14, fontWeight: 700, color: p.rank <= 3 ? 'var(--accent)' : C.dim }}>{m ?? p.rank}</div>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{p.avatar}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 700, color: p.isYou ? 'var(--accent)' : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.handle}</span>
                    {p.isYou && <span style={{ fontFamily: AF.m, fontSize: 8, color: 'var(--accent)', border: `1px solid ${C.accentBorder}`, borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>YOU</span>}
                  </div>
                  <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted }}>{p.title} · {p.specimens} specimens{isMobile ? '' : ` · ${p.lastActive}`}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 700, color: C.text }}>{p.score.toLocaleString()}</div>
                  <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.08em' }}>SCORE</div>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ══ WARDENS (friends, server-backed) ══════════════════════════════════════════
function FriendsView({ langCode, state, localProfile, onViewWarden }: {
  langCode: string; state: GardenState; localProfile: Profile; onViewWarden: (userId: string) => void
}) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '18px' : '48px'
  const me = useMe()
  const [q, setQ] = useState('')
  const friendsQ = useFriends(langCode, me.authenticated)
  const searchQ = useSearchWardens(q, langCode, me.authenticated)
  const toggle = useToggleFriend(langCode)

  if (me.isLoading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}><span style={{ fontFamily: AF.b, color: C.muted }}>…</span></div>
  if (!me.authenticated) return <SignInGate feature="fellow Wardens" sub="Add friends and compare your restoration of the Verdant." />

  const myScore = wardenScore(summaryFromGarden(state, langCode))
  const friendIds = new Set(me.profile?.friends ?? [])
  const friends = friendsQ.data?.friends ?? []
  const found = (searchQ.data?.wardens ?? []).filter(w => !friendIds.has(w.userId))

  const Row = ({ p, friend }: { p: LeaderboardEntry; friend: boolean }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 11, border: `1px solid ${C.border}`, background: C.surf }}>
      <button onClick={() => onViewWarden(p.userId)} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>{p.avatar}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: AF.h, fontSize: 14.5, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.handle}</div>
          <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted }}>{p.title} · {p.lastActive}</div>
        </div>
      </button>
      {friend && (() => { const d = p.score - myScore; return (
        <span style={{ fontFamily: AF.m, fontSize: 11, color: d > 0 ? 'var(--error)' : 'var(--success)', flexShrink: 0 }}>{d > 0 ? `+${d.toLocaleString()} ahead` : d < 0 ? `${(-d).toLocaleString()} behind` : 'even'}</span>
      ) })()}
      <button onClick={() => toggle.mutate({ friendId: p.userId, add: !friend })} style={{ flexShrink: 0, fontFamily: AF.b, fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', border: friend ? `1px solid ${C.border}` : 'none', background: friend ? 'transparent' : 'var(--accent)', color: friend ? C.dim : C.bg }}>{friend ? 'Remove' : '+ Add'}</button>
    </div>
  )

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: `22px ${hp} 72px` }}>
        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 12 }}>YOUR WARDENS · {friends.length}</div>
        {friends.length === 0 ? (
          <div style={{ padding: '22px 20px', borderRadius: 12, border: `1px dashed ${C.border2}`, background: C.surf, textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 26, marginBottom: 8 }}>🤝</div>
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>No fellow Wardens yet. Find some below to compare your restoration of the Verdant.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 28 }}>
            {friends.map(p => <Row key={p.userId} p={p} friend />)}
          </div>
        )}

        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 12 }}>FIND WARDENS</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name…" style={{ width: '100%', boxSizing: 'border-box', fontFamily: AF.b, fontSize: 14, color: C.text, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 9, padding: '11px 14px', marginBottom: 12, outline: 'none' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {found.slice(0, 12).map(p => <Row key={p.userId} p={p} friend={false} />)}
          {!searchQ.isLoading && found.length === 0 && <div style={{ fontFamily: AF.b, fontSize: 13, color: C.dim, padding: '8px 2px' }}>No Wardens match “{q}”.</div>}
        </div>
      </div>
    </div>
  )
}

// ══ STORY SCENE OVERLAY ═══════════════════════════════════════════════════════
function SceneOverlay({ beats, onClose }: { beats: Beat[]; onClose: () => void }) {
  const isMobile = useIsMobile()
  const [i, setI] = useState(0)
  const beat = beats[i]
  useEffect(() => { setI(0) }, [beats])
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); adv() } }
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h)
  })
  const adv = () => { if (i + 1 < beats.length) setI(i + 1); else onClose() }
  if (!beat) return null
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(5,8,5,0.86)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={adv}>
      <div className="c8-pop" style={{ maxWidth: 540, width: '100%', background: C.surf, border: `1px solid ${C.border2}`, borderRadius: 18, padding: isMobile ? '28px 22px' : '36px 34px', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <span style={{ fontSize: 16 }}>{beat.kind === 'act' ? '📖' : COMPANION.glyph}</span>
          <span style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em' }}>{beat.kind === 'act' ? 'A NEW CHAPTER' : 'THE SEEDKEEPER SPEAKS'}</span>
        </div>
        <h2 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 27, fontWeight: 700, color: C.text, letterSpacing: '-0.7px', margin: '0 0 18px' }}>{beat.title}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {beat.lines.map((line, k) => (
            <p key={k} style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, lineHeight: 1.65, margin: 0 }}>{line}</p>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 26 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {beats.map((_, k) => <span key={k} style={{ width: 6, height: 6, borderRadius: '50%', background: k === i ? 'var(--accent)' : C.border }} />)}
          </div>
          <button onClick={adv} style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '11px 26px', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            {i + 1 < beats.length ? 'Next' : 'Continue'} <Kbd>↵</Kbd>
          </button>
        </div>
      </div>
    </div>
  )
}

// ══ ACHIEVEMENT TOASTS ════════════════════════════════════════════════════════
function AchievementToasts({ items, onDismiss }: { items: Achievement[]; onDismiss: (id: string) => void }) {
  useEffect(() => {
    if (!items.length) return
    const t = setTimeout(() => onDismiss(items[0].id), 4200)
    return () => clearTimeout(t)
  }, [items, onDismiss])
  if (!items.length) return null
  return (
    <div style={{ position: 'fixed', right: 16, bottom: 76, zIndex: 1100, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
      {items.slice(0, 3).map(a => (
        <div key={a.id} className="c8-pop" onClick={() => onDismiss(a.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: C.surf, border: `1px solid ${TIER_COLOR[a.tier]}`, boxShadow: '0 10px 32px rgba(0,0,0,0.45)', cursor: 'pointer' }}>
          <span style={{ fontSize: 26, flexShrink: 0 }}>{a.glyph}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: AF.m, fontSize: 9, color: 'var(--accent)', letterSpacing: '0.12em' }}>ACHIEVEMENT UNLOCKED</div>
            <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 700, color: C.text }}>{a.name}</div>
            <div style={{ fontFamily: AF.b, fontSize: 11.5, color: C.muted }}>{a.desc}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ══ HOW IT WORKS — onboarding guide ═══════════════════════════════════════════
const GUIDE_KEY = 'c8-conjugarden-guide-v1'
const guideSeen = () => { try { return typeof window !== 'undefined' && window.localStorage.getItem(GUIDE_KEY) === '1' } catch { return false } }
const markGuideSeen = () => { try { window.localStorage.setItem(GUIDE_KEY, '1') } catch {} }

function GuideOverlay({ onClose }: { onClose: () => void }) {
  const isMobile = useIsMobile()
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' || e.key === 'Enter') { e.preventDefault(); onClose() } }
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const steps = [
    { glyph: '🧭', title: 'Set out on an expedition', body: 'Pick a biome (a band of verb tenses) and one adaptation, then choose your path through a branching trail of groves.' },
    { glyph: '✏️', title: 'Answer to grow', body: 'Type or pick the correct conjugation. Right answers build a bloom streak and earn 🌸 Pollen; a miss costs 🌿 vitality. Hit zero and the run withers.' },
    { glyph: '🔥', title: 'Beat the Conjugation Trial', body: 'Every expedition ends in a timed boss. Win it to bloom the biome and carry your whole harvest home.' },
    { glyph: '🏡', title: 'Spend, grow, go deeper', body: 'Bank 🌸 Pollen in the Greenhouse for permanent upgrades. Master a verb and it grows into a 🌿 specimen; meet a new one and it joins your 📒 Verbarium.' },
  ]
  const glossary = [
    ['🌫️ the Hush', 'the blight you push back'],
    ['🌸 Pollen', 'currency you bank & spend'],
    ['🌿 vitality', 'your lives in a run'],
    ['🔥 bloom streak', 'combo — more Pollen'],
    ['🪵 Heartwood', 'prestige currency'],
    ['🌀 Depth', 'optional extra difficulty'],
  ]

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(5,8,5,0.86)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, overflowY: 'auto' }}>
      <div className="c8-pop" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto', background: C.bg, border: `1px solid ${C.border2}`, borderRadius: 18, padding: isMobile ? '24px 20px' : '32px 34px', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>
        <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', marginBottom: 8 }}>HOW THE CONJUGARDEN WORKS</div>
        <h2 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 27, fontWeight: 700, color: C.text, letterSpacing: '-0.7px', margin: '0 0 12px' }}>A roguelite where conjugating <em>is</em> the game</h2>

        {/* premise */}
        <div style={{ display: 'flex', gap: 12, padding: '14px 16px', borderRadius: 12, border: `1px solid ${C.border}`, background: C.surf, marginBottom: 18 }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>🌫️</span>
          <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, lineHeight: 1.6, margin: 0 }}>
            A blight called <strong style={{ color: C.text }}>the Hush</strong> has silenced the world's living words. You're a <strong style={{ color: C.text }}>Warden</strong> — you speak them back. <strong style={{ color: 'var(--accent)' }}>Every conjugation you get right restores a little more of the world.</strong> That's the whole story: practice <em>is</em> the cure.
          </p>
        </div>

        {/* the loop */}
        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 12 }}>THE LOOP</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 9, background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>{s.glyph}</div>
              <div>
                <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 2 }}><span style={{ color: 'var(--accent)', fontFamily: AF.m, fontSize: 12, marginRight: 7 }}>{i + 1}</span>{s.title}</div>
                <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>

        {/* glossary */}
        <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 10 }}>QUICK GLOSSARY</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 6, marginBottom: 22 }}>
          {glossary.map(([term, def]) => (
            <div key={term} style={{ display: 'flex', alignItems: 'baseline', gap: 7, fontFamily: AF.b, fontSize: 12.5 }}>
              <span style={{ color: C.text, fontWeight: 700, whiteSpace: 'nowrap' }}>{term}</span>
              <span style={{ color: C.muted }}>— {def}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: AF.b, fontSize: 15, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '12px 28px', borderRadius: 10, cursor: 'pointer' }}>Start growing <Kbd>↵</Kbd></button>
          <span style={{ fontFamily: AF.b, fontSize: 12, color: C.dim }}>Reopen anytime from the <strong style={{ color: C.muted }}>?</strong> button.</span>
        </div>
      </div>
    </div>
  )
}

// ══ PAGE ══════════════════════════════════════════════════════════════════════
type Phase = 'hub' | 'shop' | 'setup' | 'run' | 'chronicle' | 'verbarium' | 'profile' | 'leaderboard' | 'friends'

function GardenApp() {
  const pageContext = usePageContext()
  const langCode = (pageContext.routeParams as { language?: string } | undefined)?.language ?? 'es'
  const languageName = langCodeToName(langCode)
  const isMobile = useIsMobile()
  const mounted = useMounted()
  const hp = isMobile ? '16px' : '48px'

  const authName = (pageContext as any).user?.name ?? null
  const me = useMe()
  const syncGarden = useSyncGarden()

  const [garden, setGarden] = useState<GardenState>(() => readGarden(langCode))
  const [phase, setPhase] = useState<Phase>('hub')
  const [verbData, setVerbData] = useState<Record<string, any>>({})
  const [verbLoading, setVerbLoading] = useState(true)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [viewWarden, setViewWarden] = useState<string | null>(null)
  const [scene, setScene] = useState<Beat[]>([])
  const [toasts, setToasts] = useState<Achievement[]>([])
  const [showGuide, setShowGuide] = useState(false)

  // Show the "how it works" guide once for brand-new Wardens (after any intro scene).
  // Mark seen immediately when shown — navigation away before dismissal shouldn't re-trigger it.
  useEffect(() => {
    if (!mounted || phase !== 'hub' || scene.length > 0) return
    if (garden.stats.runs === 0 && masteredSpecimens(garden).length === 0 && !guideSeen()) {
      setShowGuide(true)
      markGuideSeen()
    }
  }, [mounted, phase, scene.length, garden])
  const closeGuide = useCallback(() => { setShowGuide(false) }, [])

  // Load (and reload on language change) — garden state is per-language.
  useEffect(() => {
    setGarden(readGarden(langCode))
    setProfile(readProfile(authName))
    setViewWarden(null)
    // Restore an in-flight expedition if the page was refreshed mid-run.
    const savedRun = readRun(langCode)
    if (savedRun && savedRun.status !== 'won' && savedRun.status !== 'lost') {
      committedRef.current = null
      dispatch({ type: 'RESTORE', payload: savedRun })
      setPhase('run')
    } else {
      setPhase('hub')
    }
    setVerbLoading(true)
    loadVerbData(langCode).then(d => { setVerbData(d); setVerbLoading(false) })
  }, [langCode])

  // Publish the garden summary to the server whenever it changes (if signed in).
  useEffect(() => {
    if (!mounted || !me.authenticated) return
    syncGarden.mutate(summaryFromGarden(garden, langCode))
    // syncGarden intentionally omitted from deps (its identity changes each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garden, me.authenticated, langCode, mounted])

  const persist = useCallback((next: GardenState) => { setGarden(next); writeGarden(langCode, next) }, [langCode])

  // Surface story beats + achievements as they unlock, then mark them seen.
  useEffect(() => {
    if (!mounted) return
    const beats = pendingBeats(garden)
    const fresh = newlyUnlocked(garden)
    if (!beats.length && !fresh.length) return
    if (beats.length) setScene(beats)
    if (fresh.length) setToasts(t => [...t, ...fresh])
    const next: GardenState = {
      ...garden,
      seenBeats: [...garden.seenBeats, ...beats.map(b => b.id)],
      seenAchievements: [...garden.seenAchievements, ...fresh.map(a => a.id)],
    }
    setGarden(next); writeGarden(langCode, next)
  }, [garden, mounted, langCode])

  const saveProfile = useCallback((p: Profile) => { setProfile(p); writeProfile(p) }, [])
  const openTab = useCallback((t: Tab) => { setViewWarden(null); setPhase(t === 'garden' ? 'hub' : t) }, [])
  const openWarden = useCallback((userId: string) => { setViewWarden(userId); setPhase('profile') }, [])
  const dismissToast = useCallback((id: string) => setToasts(t => t.filter(a => a.id !== id)), [])

  // ── Section-wide keyboard shortcuts (tabs, hub actions, back) ──
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (phase === 'run' || scene.length > 0) return                 // run / story-scene own the keyboard
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const k = e.key.toLowerCase()
      if (e.key === 'Escape') { if (viewWarden) setViewWarden(null); else if (phase === 'shop' || phase === 'setup') setPhase('hub'); return }
      // 1–6 jump to a tab — only on tab screens (setup/shop use digits for their own picks)
      const onTab = phase === 'hub' || phase === 'chronicle' || phase === 'verbarium' || phase === 'profile' || phase === 'leaderboard' || phase === 'friends'
      const di = ['1', '2', '3', '4', '5', '6'].indexOf(e.key)
      if (onTab && di >= 0 && di < TABS.length) { e.preventDefault(); openTab(TABS[di].id); return }
      if (phase === 'hub') {
        if (k === 'e') { e.preventDefault(); setPhase('setup') }
        else if (k === 'g') { e.preventDefault(); setPhase('shop') }
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [phase, scene.length, viewWarden, openTab])

  const [run, dispatch] = useReducer(runReducer, null as unknown as RunState)

  // Build the branching run-map up front (3 path columns + a boss), then start.
  const launch = useCallback((biome: Biome, traitId: TraitId, depth: number) => {
    const supported = supportedFor(langCode)
    const tenses = biome.tenses.filter(t => supported.has(t))
    const pronouns = pronounsFor(langCode)
    const cfg = depthConfig(depth)
    const poolCards = generateDrillCards({ data: verbData, langCode, pronouns, tenses, verbType: 'all', count: 80 })
    const poolAnswers = poolCards.map(c => c.answer)

    // Weak / SRS-due verbs become "elite" encounters worth double (Phase 6).
    const weak = computeVerbLeaderboard(2).slice(0, 12).map(e => e.infinitive)
    const due = getWordsNeedingReview(5).slice(0, 12).map(w => w.term)
    const eliteSet = new Set<string>([...weak, ...due])
    const mkCard = (c: DrillCardData, i: number, forceElite = false): RunCard => {
      const mode: 'type' | 'quiz' = i % 2 === 1 ? 'quiz' : 'type'
      let base: RunCard
      if (mode === 'quiz') {
        // Build distractors from same verb + same tense, different pronouns.
        // For PT/FI, deriveConjugation returns infinitive as fallback, so we read
        // irregular_forms directly using the tense title prefix instead.
        let sameVerbConjs: string[]
        if (langCode === 'pt' || langCode === 'fi') {
          const tenseTitle = langCode === 'pt' ? TENSE_TITLE_PT[c.tense as ConjTense] : TENSE_TITLE_FI[c.tense as ConjTense]
          const forms = verbData[c.verb]?.irregular_forms ?? {}
          sameVerbConjs = tenseTitle
            ? Object.entries(forms)
                .filter(([k]) => k.startsWith(tenseTitle + ';'))
                .map(([, v]) => v)
                .filter(v => v && v !== c.answer)
            : []
        } else {
          sameVerbConjs = pronounsFor(langCode)
            .map(p => deriveConjugation(c.verb, p, c.tense as ConjTense, langCode, verbData))
            .filter(a => a && a !== c.verb)
        }
        const uniq = Array.from(new Set(sameVerbConjs))
        const pool = uniq.length >= 3 ? uniq : poolAnswers
        base = { ...c, mode, options: buildOptions(c.answer, pool) }
      } else {
        base = { ...c, mode }
      }
      return forceElite || eliteSet.has(c.verb) ? { ...base, elite: true } : base
    }
    const gen = (vt: 'all' | 'irregular', count: number) => {
      let raw = generateDrillCards({ data: verbData, langCode, pronouns, tenses, verbType: vt, count, priorityVerbs: [...eliteSet] })
      if (raw.length < Math.min(3, count)) raw = generateDrillCards({ data: verbData, langCode, pronouns, tenses, verbType: 'all', count })
      return raw
    }

    let n = 0
    const mkThicket = (col: number): RunNode => {
      const vt: 'all' | 'irregular' = Math.random() < cfg.irregularBias + col * 0.12 ? 'irregular' : 'all'
      const cards = gen(vt, CARDS_PER_GROVE + (col >= 2 ? 1 : 0)).map((c, i) => mkCard(c, i))
      return { id: `n${n++}`, kind: 'thicket', label: 'Thicket', glyph: '🌿', desc: 'A balanced grove of the wild. Steady spores.', cards }
    }
    const mkWarren = (): RunNode => {
      const cards = gen('irregular', 4).map((c, i) => mkCard(c, i, true))
      return { id: `n${n++}`, kind: 'warren', label: 'Warren', glyph: '◆', desc: 'An elite tangle — tougher verbs, ×2 spores.', cards }
    }
    const mkGlade = (): RunNode => ({ id: `n${n++}`, kind: 'glade', label: 'Glade', glyph: '✨', desc: 'A quiet clearing. Take a boon instead of a fight.' })
    const mkSpring = (): RunNode => ({ id: `n${n++}`, kind: 'spring', label: 'Mirror Spring', glyph: '🪞', desc: 'Rest by still water. Heal and steady yourself.' })

    const map: RunNode[][] = []
    for (let col = 0; col < NORMAL_GROVES; col++) {
      const a = Math.random() < 0.55 ? mkThicket(col) : mkWarren()           // always a fight
      const roll = Math.random()
      const b = roll < 0.4 ? (a.kind === 'thicket' ? mkWarren() : mkThicket(col)) : roll < 0.72 ? mkGlade() : mkSpring()
      map.push([a, b])
    }
    // Boss column — the Conjugation Trial.
    const bossCards = gen('irregular', BOSS_BASE_CARDS + cfg.extraBossCards).map((c, i) => mkCard(c, i))
    map.push([{ id: `boss`, kind: 'boss', label: 'Conjugation Trial', glyph: '🔥', desc: 'The Hush coalesces into one voice. Beat the clock.', cards: bossCards }])

    if (!bossCards.length) return
    const baseline: Record<string, number> = {}
    for (const [verb, sp] of Object.entries(garden.specimens)) baseline[verb] = sp.correct
    const discoveredVerbs = Object.keys(garden.discovered)
    const comboCap = 3 + (garden.oldGrowth['combocrown'] ?? 0) * 0.5
    const rerolls = 1 + (garden.oldGrowth['reroll'] ?? 0)
    const maxVitality = garden.maxVitality + (garden.oldGrowth['heartvitality'] ?? 0)
    dispatch({ type: 'START', payload: { biome, langCode, traits: [traitId], maxVitality, map, baseline, discoveredVerbs, depth, comboCap, rerolls } })
    setPhase('run')
  }, [verbData, langCode, garden])

  // Commit a finished/abandoned run into the garden. Idempotent (guarded by
  // committedRef) and clears the saved run so a refresh won't replay it.
  const committedRef = useRef<RunState | null>(null)
  const commitRun = useCallback((r: RunState) => {
    if (!r || committedRef.current === r) return
    committedRef.current = r
    const won = r.status === 'won'
    const withered = r.status === 'lost'
    const next: GardenState = JSON.parse(JSON.stringify(garden))
    // merge per-verb correct counts + mastery
    for (const [verb, gained] of Object.entries(r.runCorrect)) {
      const existing = next.specimens[verb] ?? { verb, correct: 0, mastered: false, biome: r.biome.id, bornAt: 0 }
      existing.correct += gained
      if (!existing.mastered && existing.correct >= MASTERY_THRESHOLD) {
        existing.mastered = true
        existing.bornAt = Date.now()
        if (!existing.biome) existing.biome = r.biome.id
      }
      next.specimens[verb] = existing
    }
    // catalogue any wild words discovered this run (kept even on a loss — it's learning)
    for (const [verb, rarity] of Object.entries(r.discoveries)) {
      if (!next.discovered[verb]) next.discovered[verb] = { verb, rarity, biome: r.biome.id, firstSeen: Date.now() }
    }
    // Stakes: a win banks all spores, a retreat banks 60%, withering forfeits the unbanked harvest.
    const banked = bankedPollen(r, garden)
    next.pollen += banked
    next.stats.runs += 1
    next.stats.totalPollen += banked
    next.stats.totalCorrect += Object.values(r.runCorrect).reduce((a, b) => a + b, 0)
    next.stats.bestDepth = Math.max(next.stats.bestDepth, won ? r.map.length : Math.max(0, r.mapPos))
    next.stats.bestStreak = Math.max(next.stats.bestStreak ?? 0, r.bestCombo)
    next.stats.reviewFixed = (next.stats.reviewFixed ?? 0) + r.reviewHits.length
    if (withered) next.stats.withered += 1
    if (won) {
      next.stats.wins = (next.stats.wins ?? 0) + 1
      next.stats.bosses = (next.stats.bosses ?? 0) + 1
      next.clears[r.biome.id] = (next.clears[r.biome.id] ?? 0) + 1
      next.bossClears[r.biome.id] = (next.bossClears[r.biome.id] ?? 0) + 1
      next.depth[r.biome.id] = Math.max(next.depth[r.biome.id] ?? 0, r.depth)
      next.heartwood = (next.heartwood ?? 0) + HEARTWOOD_PER_BOSS + r.depth
    }
    persist(next)
    clearRun(langCode)
  }, [garden, persist, langCode])

  const finishRun = useCallback(() => {
    if (run) commitRun(run)
    clearRun(langCode)
    setPhase('hub')
  }, [run, commitRun, langCode])

  // Persist the in-flight run so a mid-expedition refresh restores it exactly.
  useEffect(() => {
    if (!mounted) return
    if (run && run.status !== 'won' && run.status !== 'lost') writeRun(langCode, run)
    else clearRun(langCode)
  }, [run, mounted, langCode])

  // Commit the instant a run ends, so refreshing on the summary can't lose rewards.
  useEffect(() => {
    if (run && (run.status === 'won' || run.status === 'lost')) commitRun(run)
  }, [run, commitRun])

  const buy = useCallback((item: ShopItem) => {
    const hw = item.currency === 'heartwood'
    if ((hw ? garden.heartwood : garden.pollen) < item.cost) return
    const next: GardenState = JSON.parse(JSON.stringify(garden))
    if (hw) next.heartwood -= item.cost; else next.pollen -= item.cost
    if (item.id === 'vitality') {
      const tier = VITALITY_TIERS.indexOf(next.maxVitality)
      if (tier < VITALITY_TIERS.length - 1) next.maxVitality = VITALITY_TIERS[tier + 1]
    } else if (item.id === 'compost') {
      const tier = COMPOST_TIERS.findIndex(t => Math.abs(t - next.pollenMultiplier) < 0.001)
      if (tier < COMPOST_TIERS.length - 1) next.pollenMultiplier = COMPOST_TIERS[tier + 1]
    } else if (item.id.startsWith('biome:')) {
      const id = item.id.slice(6) as BiomeId
      if (!next.unlockedBiomes.includes(id)) next.unlockedBiomes.push(id)
    } else if (item.id.startsWith('trait:')) {
      const id = item.id.slice(6) as TraitId
      if (!next.unlockedTraits.includes(id)) next.unlockedTraits.push(id)
    } else if (item.id.startsWith('og:')) {
      const id = item.id.slice(3)
      next.oldGrowth[id] = (next.oldGrowth[id] ?? 0) + 1   // applied at run start in launch()
    }
    persist(next)
  }, [garden, persist])

  const doReset = useCallback(() => {
    if (typeof window !== 'undefined' && window.confirm('Reset your entire garden for this language? Specimens, Pollen and unlocks will be lost.')) {
      clearRun(langCode)
      committedRef.current = null
      setGarden(resetGarden(langCode))
      setPhase('hub')
    }
  }, [langCode])

  // ── Header ──
  const PHASE_META: Record<Phase, { icon: string; title: string; desc: string }> = {
    hub: { icon: '🌿', title: 'Conjugarden', desc: 'Grow a living language ecosystem, one expedition at a time.' },
    shop: { icon: '🏡', title: 'Greenhouse', desc: 'Spend Pollen on permanent upgrades.' },
    setup: { icon: '🧭', title: 'Expedition', desc: 'Pick your biome and adaptation.' },
    run: { icon: '🌸', title: 'Expedition', desc: 'Answer to grow. Miss and you wither.' },
    chronicle: { icon: '📖', title: 'The Chronicle', desc: 'Your stand against the Hush, chapter by chapter.' },
    verbarium: { icon: '📒', title: 'Verbarium', desc: 'Every species you have met in the wild — discovered and caught.' },
    profile: { icon: '🪪', title: viewWarden ? 'Warden' : 'Your Warden', desc: viewWarden ? "A Warden's public card." : 'Your public card — achievements and standing.' },
    leaderboard: { icon: '🏆', title: 'Leaderboard', desc: 'How your restoration of the Verdant ranks.' },
    friends: { icon: '🤝', title: 'Wardens', desc: 'Fellow keepers of the Verdant. Add them, compare progress.' },
  }
  const meta = PHASE_META[phase]
  const activeTab: Tab = (phase === 'shop' || phase === 'setup' || phase === 'hub') ? 'garden'
    : phase === 'run' ? 'garden' : (phase as Tab)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>
      {phase !== 'run' && (
        <div style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.55, WebkitMaskImage: 'linear-gradient(to left, #000, transparent 60%)', maskImage: 'linear-gradient(to left, #000, transparent 60%)' }}>
            <MotifBand language={languageName} height={isMobile ? 92 : 104} opacity={0.7} />
          </div>
          <div style={{ position: 'relative', padding: `20px ${hp} 16px` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 7 }}>
                  Experimental · {motifLabel(languageName)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
                  <span style={{ fontSize: 18 }}>{meta.icon}</span>
                  <h1 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 26, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.7px' }}>{meta.title}</h1>
                </div>
                <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: 0 }}>{meta.desc}</p>
              </div>
              <div style={{ flexShrink: 0, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setShowGuide(true)} title="How the Conjugarden works" aria-label="How it works" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: C.muted, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 20, padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <span style={{ fontFamily: AF.h, fontWeight: 700 }}>?</span> How it works
                </button>
                <AuthChip />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section tabs (hidden mid-expedition) */}
      {mounted && profile && phase !== 'run' && (
        <TabBar active={activeTab} onSelect={openTab} friendCount={profile.friends.length} />
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!mounted || !profile ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
            <span style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>Waking the garden…</span>
          </div>
        ) : phase === 'hub' ? (
          <Hub state={garden} langCode={langCode} onStartExpedition={() => setPhase('setup')} onOpenShop={() => setPhase('shop')} onReset={doReset} onOpenChronicle={() => setPhase('chronicle')} />
        ) : phase === 'shop' ? (
          <Shop state={garden} langCode={langCode} onBuy={buy} onBack={() => setPhase('hub')} />
        ) : phase === 'setup' ? (
          <Setup state={garden} langCode={langCode} loading={verbLoading} onLaunch={launch} onBack={() => setPhase('hub')} />
        ) : phase === 'chronicle' ? (
          <ChronicleView state={garden} />
        ) : phase === 'verbarium' ? (
          <VerbariumView state={garden} verbData={verbData} langCode={langCode} />
        ) : phase === 'profile' ? (
          <ProfileView langCode={langCode} state={garden} localProfile={profile} onSaveLocal={saveProfile} viewUserId={viewWarden} />
        ) : phase === 'leaderboard' ? (
          <LeaderboardView langCode={langCode} onViewWarden={openWarden} />
        ) : phase === 'friends' ? (
          <FriendsView langCode={langCode} state={garden} localProfile={profile} onViewWarden={openWarden} />
        ) : run ? (
          <RunView state={garden} langCode={langCode} run={run} dispatch={dispatch} onAbort={finishRun} />
        ) : null}
      </div>

      {scene.length > 0 && <SceneOverlay beats={scene} onClose={() => setScene([])} />}
      {showGuide && <GuideOverlay onClose={closeGuide} />}
      <AchievementToasts items={toasts} onDismiss={dismissToast} />
    </div>
  )
}

// ── Wrap the whole section in the TanStack Query provider ────────────────────
export default function Page() {
  return (
    <ConjugardenProvider>
      <GardenApp />
    </ConjugardenProvider>
  )
}
